import { useEffect, useState, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { motion, useInView } from "framer-motion";
import { Toaster, toast } from "sonner";
import { StoreProvider, useStore, CartDrawer, CartButton, PaymentResult, BUNDLE_SKUS, goldConfetti } from "./store";
import { GuidesSection, ShippingInfo, GuidePage } from "./guides";
import { EmailHint, GiftGuide, ProductMeta, ReviewPage, AdminDashboard, InventoryCard, ReviewsAdmin, LeaderboardCard, ReviewHighlights, VipCard, GiftCards, ProductPhotosCard, GiftCardsTable, HolidayBanner, CampaignCard, ManualVipCard } from "./extras";
import {
  Crown,
  Menu,
  X,
  Leaf,
  ShieldCheck,
  Sparkles,
  Droplets,
  Instagram,
  Mail,
  MapPin,
  Star,
  ArrowRight,
  MessageCircle,
  Send,
  ChevronDown,
  Download,
  Smartphone,
  Share,
  Flag,
  Package,
  CheckCircle2,
  Building2,
  Truck,
  Gift,
  Volume2,
  VolumeX,
  Share2,
  Play,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


const LOGO = "/logo-200.webp";
const LOGO_LG = "/logo-640.webp";

const IMG = {
  lineup:
    "https://images.unsplash.com/photo-1633793566189-8e9fe6f817fc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  botanical:
    "https://images.unsplash.com/photo-1598559213718-da4a925fbf71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  moistEyeCream:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/614953e23c213fb6e79561f8ac951d7a4621866031fd9f791a2580fd0a340029.jpeg",
  microdermScrub:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/951728c99608f737894716fb8defc4b0c73dd3a9e19f0668668f9af82e062c3a.jpeg",
  kojicScrub:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/7a11299a1804aed54fcdc235b4ffddc4ff83f660ef883005d5bb7b535336ba56.jpeg",
  mineralSpf:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/f8661adf1c3f7768971d32972db9ef472bf411ffe106750e4e694d486e01a524.jpeg",
  oilFreeMoist:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/4250e457a4c45c2d671ef7ce51566123db1a7fb90ea83690e8d2bb6322bbb30d.jpeg",
  dnaMoist:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/ad88ce0c61c7f5d164a1fb9556bc2290a534db1c81c5339b4466e18e61cdceaa.jpeg",
  vitCESerum:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/7c5d4ab2e11a69dc35abda53c544f8fbb3c885385d9fb2ada1572b499b14386b.jpeg",
  creamyBalm:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/da025a4d62a91fb7f1a101b5074b29fe5c3886365aab58d6eaa0d4be7c717c28.jpeg",
  glycolicCleanser:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/cc240c083d9201f9baa96789270ec3a4969a10338046dc75dc73cee316b0ad6c.jpeg",
  goldMask:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/7a51b517c12261a42e3d8deae79f3f9a1fcf76dda34d7e65692e741e8b92e08e.jpeg",
  goldEyePatch:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/bfe63b01a42e8f6b64405a2f62eba3033436d64af63d80d1bd064040246bafea.jpeg",
  hydraToner:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/4daeb7966ea7c0e0e7c6d86f456c1aac3a6934ce988a5bc7a7e95e571c233621.jpeg",
  bundleSet:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/c1e50b560f633bf56d39fd7e5410739a8881fcb6704b82307770cf3c4240b8fb.jpeg",
  cleanBundle:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/fcefb5302aad8c1b4c76ecd29a84456d3bf700b1f12e729587a163c3b41fd282.jpeg",
  beardedBundle:
    "/bundle-mane-tan.jpg",
  blueGrit:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/1718ea3fbd39b0095d65c931ace3df59b31abc23fdf7c18fff4a9a9b86bb1402.jpeg",
  ingrownSerum:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/5edb8444f147faf5552a1028c1c08c9ddad101176389cd1cd3038fa0730933cf.jpeg",
  eyeLiftingCream:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/8872337e1f2a66185d0ce8019c13327191e8f2a1202075ae9e6fd1107ee32ce5.jpeg",
  goldHalfMask:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/17b93769730005d5e08d305888a3f21901268e08bd30a72e34416f5d2cb89426.jpeg",
  antiAgingPads:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/5f08a5bb3cb6003c267b637ab2d9fc418d978643d25fd7a9c87f56ae8af013ec.jpeg",
  vitaminCBoost:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/7d4cc3ca4848ca3ce58cfdab679ded3e1c922cc519696023ff875872efaf5cae.jpeg",
  haZinc:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/8bea7cc2adb391d1889ece9e1a987f5e4266bdf1bf502f2b1e1c48c46e7e0c48.jpeg",
  manWash1:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/9a3cc874c2556f7e1c5a59e8f5c149a56466a43a35dca51fbcc9132e067e6432.jpeg",
  manWash2:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/5b3b85c85280a6bba6d4851f7493a690b4ab288136f44a04f724e5472811b17a.jpeg",
  manCream:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/8f512e1643ce4050971af97a9068dd74f90c5e6c5c0d13f519e6300dc1fa9e9a.jpeg",
  manSerum:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/a81ce2acb1f14a552fc46611eded0ba66b541e2198d3e883a9849f9958aea89d.jpeg",
  beardOil:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/b4efbc6bccda804a43df55f919483f82b7b9e1527cc85db3e9da29756738f802.jpeg",
  founder:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/3560b86e756a448fdf64673536a3f1f34aa651957f8adc6af6f768f584755b3a.jpeg",
  usaSeal:
    "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/388011e50547c17523bc40c6598a5c99d4520747ebf7895ff5dc33fdec72761a.jpeg",
};

// Real Royal King Skin Co. catalog (live products from Shopify). Prices pending from owner.
const PRODUCTS = [
  { name: "Moisturizing Eye Cream (1.7 oz)", price: 68, tag: "Eye Care", desc: "The delicate skin above, around and below the eye needs special care to fend off fine lines, wrinkles and crepiness. This highly effective cream tackles all three — reviving the eye area with a renewed, youthful look.", img: IMG.moistEyeCream },
  { name: "Hydra Toner (4 oz)", price: 48, tag: "Toner", desc: "Refreshing hydra toner that balances, hydrates and preps skin for the rest of your routine.", img: IMG.hydraToner },
  { name: "24K Gold Under Eye Patch (60 Count)", price: 60, tag: "Eye Care", desc: "Luxurious 24K gold under-eye patches that depuff, brighten and smooth the delicate eye area. 60 count.", img: IMG.goldEyePatch },
  { name: "Microdermabrasion Scrub", price: 55, tag: "Exfoliator", desc: "Resurfacing scrub that polishes away dead skin for a smooth, refined finish.", img: IMG.microdermScrub },
  { name: "Exfoliation Scrub with Kojic Acid (1.7 oz)", price: 55, tag: "Exfoliator", desc: "Brightening exfoliation scrub with Kojic acid that buffs away dullness and evens skin tone. 1.7 oz.", img: IMG.kojicScrub },
  { name: "Blue Clay Exfoliation Mask (1.7 oz)", price: 88, tag: "Mask", desc: "Your weekly reset. An exfoliating clay mask with blue clay and kaolin clay, plus glycolic acid, salicylic acid, niacinamide and botanical brighteners, to help brighten and smooth for a more refined-looking complexion. Made for dull, uneven skin.", img: IMG.blueGrit },
  { name: "24K Gold Mask — Full Mask", price: 23, tag: "Gold Mask", desc: "Luxurious 24K gold full sheet mask infused with hyaluronic acid, vitamin C and antioxidants to brighten, firm and transform your complexion — for a radiant, healthy glow.", img: IMG.goldMask },
  { name: "24K Gold Mask — Half Mask", price: 22, tag: "Gold Mask", desc: "Top-half 24K gold mask designed for men with beards — covers the forehead and eye area to brighten, firm and depuff while leaving your beard free.", img: IMG.goldHalfMask },
  { name: "Mineral SPF 50 (1.7 oz)", price: 82, tag: "Sunscreen", desc: "Protect the crown. A broad-spectrum mineral sunscreen with zinc oxide and titanium dioxide that helps protect against UVA and UVB rays for daily wear, with antioxidant botanicals, hyaluronic acid and vitamin E. For all skin types. Proudly made in the USA.", img: IMG.mineralSpf },
  { name: "Oil-Free Moisturizer (1.7 oz)", price: 95, tag: "Moisturizer", desc: "Hydration that works as hard as you do. Lightweight and oil-free, it hydrates without a heavy or greasy feel. Hyaluronic acid, glycerin, niacinamide and pro-vitamin B5 leave skin looking balanced and comfortable. For all skin types, especially oily and combination.", img: IMG.oilFreeMoist },
  { name: "DNA Moisturizer (1.7 oz)", price: 75, tag: "Moisturizer", desc: "Advanced DNA moisturizer that deeply nourishes, repairs and revitalizes for resilient, healthy-looking skin. 1.7 oz.", img: IMG.dnaMoist },
  { name: "Eye Lifting Cream (0.5 oz)", price: 68, tag: "Eye Care", desc: "Firming eye lifting cream that tightens, depuffs and smooths the delicate eye area. 0.5 oz.", img: IMG.eyeLiftingCream },
  { name: "HA + Zinc Serum", price: 75, tag: "Serum", desc: "Hydrating hyaluronic acid and zinc serum that plumps, calms and clarifies the skin.", img: IMG.haZinc },
  { name: "Ingrown Hair Serum (1.7 oz)", price: 68, tag: "Treatment", desc: "Kings don't bump. They glide. Made in the USA, this targeted serum helps address the appearance of ingrown hairs while it hydrates and soothes. An exfoliating complex of salicylic and glycolic acid works with tea tree oil, allantoin and aloe vera for a smoother look and feel wherever ingrowns like to show up.", img: "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/ef5b5cebfc8e62c165c6b7ca816024f0b01052386c6fad7bf5da6fbffd757b48.jpeg" },
  { name: "Vitamin C Boost (1 oz)", price: 75, tag: "Serum", desc: "Concentrated vitamin C boost that brightens, evens tone and revives dull, tired skin. 1 oz.", img: IMG.vitaminCBoost },
  { name: "Vitamin C + E Serum with Ferulic Acid", price: 95, tag: "Serum", desc: "Antioxidant trio with ferulic acid that brightens, protects and defends against daily environmental stress.", img: IMG.vitCESerum },
  { name: "Creamy Cleansing Balm (4 oz / 120 mL)", price: 58, tag: "Cleanser", desc: "The first move of every King. A hydrating balm that transforms into a silky oil to help melt away dirt, sunscreen and impurities, leaving skin feeling soft and conditioned. Shea butter, jojoba oil, squalane, sweet almond oil and vitamin E. For all skin types, especially dry skin.", img: IMG.creamyBalm },
  { name: "Fresh Reign Glycolic Cleanser", price: 40, tag: "Cleanser", desc: "Gentle glycolic-and-probiotic cleanser that renews skin while protecting its natural barrier.", img: IMG.glycolicCleanser },
  { name: "Anti-Aging Pads + TCA (50 Count)", price: 78, tag: "Treatment", desc: "Resurfacing anti-aging pads with TCA that exfoliate, renew and visibly smooth for firmer, more youthful skin. 50 count.", img: IMG.antiAgingPads },
  { name: "Royal Reserve", price: 472, compareAt: 614.50, save: 142.50, bonus: "Mineral SPF 50", tag: "Bundle", desc: "The complete royal regimen in one crowning collection — Exfoliation Scrub with Kojic Acid, Ingrown Hair Serum, Vitamin C Boost, DNA Moisturizer, Anti-Aging Pads + TCA, Eye Lifting Cream and Oil-Free Moisturizer — plus a BONUS Mineral SPF 50.", img: IMG.bundleSet },
];

const BENEFITS = [
  { icon: Leaf, title: "Naturally Derived", desc: "Botanical actives, no harsh sulfates or parabens." },
  { icon: ShieldCheck, title: "Made for Men", desc: "Simple, effective skincare built around men's skin." },
  { icon: Droplets, title: "Deep Hydration", desc: "Locks in moisture for an all-day healthy finish." },
  { icon: Sparkles, title: "Made in the USA", desc: "Carefully crafted, quality formulations you can trust." },
];

const REVIEWS = [
  { name: "Darius M.", quote: "My skin has never looked better. The whole routine is pure gold." },
  { name: "Elijah R.", quote: "Finally a grooming line built for men. Premium in every way." },
  { name: "Marcus T.", quote: "Three steps, two minutes, done. My skin looks sharp and I actually stick with it." },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ---------------- Top Banner + Countdown ---------------- */
const CountUnit = ({ v, l, id }) => (
  <span data-testid={`countdown-${id}`} className="inline-flex items-baseline gap-0.5 rounded-sm bg-black/85 px-1.5 py-0.5 text-[#d4af37] font-mono tabular-nums">
    {String(v).padStart(2, "0")}<span className="text-[9px] text-[#d4af37]/70">{l}</span>
  </span>
);

const TopBanner = () => {
  const { left, spots, storeOpen, vip, launch } = useStore();
  const live = storeOpen;
  const vipEarly = vip && !launch.store_open && left !== 0;
  const s = Math.floor((left || 0) / 1000);
  return (
    <button
      data-testid="top-offer-banner"
      onClick={() => scrollTo(live ? "products" : "offer")}
      className="group w-full bg-gradient-to-r from-[#8a6d2f] via-[#d4af37] to-[#8a6d2f] px-4 py-2 text-center text-black"
    >
      {live ? (
        <span data-testid="banner-live" className="inline-flex items-center justify-center gap-2 text-xs sm:text-sm font-bold uppercase tracking-[0.15em]">
          <Crown size={14} /> {vipEarly ? "VIP Early Access — Shop Before Everyone" : "We're LIVE — Shop Now"} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
        </span>
      ) : (
        <span className="inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.12em]">
          <Crown size={13} className="shrink-0" />
          <span>The First 100 Kings Get It at the Exclusive Price on Launch Day.</span>
          <span className="font-bold italic normal-case tracking-normal text-[12px] sm:text-sm">The clock is ticking — will you make the cut?</span>
          {left === null ? (
            <span data-testid="banner-tagline" className="font-bold normal-case tracking-normal text-[12px] sm:text-sm">Kings don't wait. They move.</span>
          ) : (
            <span data-testid="banner-countdown" className="inline-flex items-center gap-1 text-xs">
              <CountUnit id="days" v={Math.floor(s / 86400)} l="D" />
              <CountUnit id="hours" v={Math.floor((s % 86400) / 3600)} l="H" />
              <CountUnit id="mins" v={Math.floor((s % 3600) / 60)} l="M" />
              <CountUnit id="secs" v={s % 60} l="S" />
            </span>
          )}
          {spots !== null && (
            <span data-testid="banner-spots" className="inline-flex items-center gap-1 rounded-full bg-black px-2.5 py-0.5 text-[10px] sm:text-[11px] font-bold tracking-[0.1em] text-[#d4af37]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#d4af37] animate-pulse" />
              {spots > 0 ? `${spots} of 100 spots left` : "All 100 spots claimed"}
            </span>
          )}
          <ArrowRight size={13} className="shrink-0 transition-transform group-hover:translate-x-1" />
        </span>
      )}
    </button>
  );
};

/* ---------------- Header ---------------- */
const Header = () => {
  const { storeOpen } = useStore();
  const shopLabel = storeOpen ? "Shop Now" : "Pre-order";
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ["Throne Room", "start-here", "Start Here"],
    ["Royal Collection", "products", "Products"],
    ["Inside the Crown", "ingredients", "Ingredients"],
    ["Royal Wardrobe", "merch", "Merch"],
    ["King's Library", "guides", "Guides"],
    ["Royal Delivery", "shipping", "Shipping"],
    ["Royal Partners", "wholesale", "Wholesale"],
    ["Our Kingdom", "about", "About"],
    ["Royal Court", "contact", "Contact"],
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 ${open ? "z-[70]" : "z-50"} transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-black/70 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <TopBanner />
      <div className="max-w-7xl min-[1600px]:max-w-[1720px] mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <button data-testid="logo-home-button" onClick={() => scrollTo("hero")} className="flex items-center gap-3 group">
          <img data-testid="header-logo" src={LOGO} alt="Royal King Skin Co." className="h-12 w-auto drop-shadow-[0_0_10px_rgba(197,160,89,0.25)]" />
          <span className="hidden sm:block font-serif text-lg tracking-tight text-[#f5f5f0] group-hover:text-[#c5a059] transition-colors">
            Royal King Skin Co.
          </span>
        </button>

        <nav className="hidden min-[1600px]:flex items-center gap-7">
          {links.map(([label, id, plain]) => (
            <button key={id} data-testid={`nav-${id}`} title={plain} onClick={() => scrollTo(id)} className="whitespace-nowrap font-serif text-[15px] tracking-wide text-[#f5f5f0]/85 hover:text-[#c5a059] transition-colors">
              {label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <CartButton />
          <button data-testid="header-shop-button" onClick={() => scrollTo("products")} className="hidden sm:inline-flex items-center rounded-sm bg-[#c5a059] px-5 py-2.5 text-sm font-medium text-black hover:bg-[#d4af37] transition-colors">
            {shopLabel}
          </button>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="min-[1600px]:hidden text-[#f5f5f0] p-2">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="min-[1600px]:hidden max-h-[75vh] overflow-y-auto backdrop-blur-xl bg-black/90 border-b border-white/10 px-6 py-6 flex flex-col gap-3">
          {links.map(([label, id, plain]) => (
            <button key={id} data-testid={`mobile-nav-${id}`} onClick={() => { scrollTo(id); setOpen(false); }} className="flex items-baseline justify-between gap-4 border-b border-white/5 py-2 text-left font-serif text-lg text-[#f5f5f0]/90 hover:text-[#c5a059] transition-colors">
              {label}
              <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#c5a059]/70">{plain}</span>
            </button>
          ))}
          <button data-testid="mobile-shop-button" onClick={() => { scrollTo("products"); setOpen(false); }} className="mt-2 rounded-sm bg-[#c5a059] px-5 py-3 text-sm font-medium text-black">
            {shopLabel}
          </button>
        </div>
      )}
    </header>
  );
};

/* ---------------- Hero ---------------- */
const HeroStatus = () => {
  const { storeOpen } = useStore();
  return <span data-testid="hero-status">{storeOpen ? "Now Shipping" : "Pre-orders Open · Ships in 7–10 Days"}</span>;
};

const Hero = () => (
  <section id="hero" className="relative min-h-screen flex items-center overflow-hidden pt-52 sm:pt-32">
    <div className="absolute inset-0">
      <img src={IMG.lineup} alt="" className="w-full h-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-black/70" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">
      <div>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }} className="inline-flex items-center gap-2 rounded-sm border border-[#c5a059]/40 px-3 py-1.5 text-xs uppercase tracking-[0.25em] text-[#c5a059]">
          <Crown size={13} /> <HeroStatus />
        </motion.p>
        <div className="mt-6 flex items-center gap-3 sm:gap-6">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.9 }} className="min-w-0 flex-1 font-serif text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-[#c5a059]">
            Step in<br className="sm:hidden" /> as a Man,<br />Walk Out<br className="sm:hidden" /> a <span className="italic text-[#d4af37]">KING</span>
          </motion.h1>
          <motion.div data-testid="hero-lion-mobile" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 0.8 }} className="lg:hidden flex w-[38%] max-w-[200px] shrink-0 flex-col items-center">
            <RoaringLogo className="w-full h-auto drop-shadow-[0_0_24px_rgba(197,160,89,0.35)]" />
            <span className="mt-2 text-center text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-[#e8c877] drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)]">Tap to roar</span>
          </motion.div>
        </div>
        <button data-testid="lion-routine-mobile" onClick={openAdvisor} className="lg:hidden mt-4 inline-flex items-center gap-1.5 text-xs text-[#c5a059] hover:text-[#d4af37] transition-colors">
          Ready to find your routine?
        </button>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.9 }} className="mt-6 max-w-md text-base sm:text-lg text-[#f5f5f0]/85 font-light leading-relaxed">
          Premium men's skincare, made simple and proudly made in the USA. No 12-step routine — just a few great products that help every man look and feel his best.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65, duration: 0.9 }} className="mt-9 flex flex-col sm:flex-row gap-4">
          <button data-testid="hero-bundle-button" onClick={() => scrollTo("products")} className="inline-flex items-center justify-center gap-2 rounded-sm bg-[#c5a059] px-8 py-4 text-sm font-medium tracking-wide text-black hover:bg-[#d4af37] transition-colors">
            <Crown size={18} /> Find Your King Routine
          </button>
          <button data-testid="hero-notify-button" onClick={() => scrollTo("waitlist")} className="inline-flex items-center justify-center gap-2 rounded-sm border border-[#c5a059] px-8 py-4 text-sm font-medium tracking-wide text-[#c5a059] hover:bg-[#c5a059]/10 transition-colors">
            Join the Royal List <ArrowRight size={16} />
          </button>
        </motion.div>
        <motion.button
          data-testid="hero-install-cue"
          onClick={() => window.dispatchEvent(new Event("rk-open-install"))}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.85, duration: 0.9 }}
          className="mt-6 group inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#f5f5f0]/60 hover:text-[#c5a059] transition-colors"
        >
          <Smartphone size={14} className="text-[#c5a059]" />
          Now available as an app
          <span className="text-[#c5a059] group-hover:translate-x-0.5 transition-transform">— add to your phone</span>
        </motion.button>

        <motion.div
          data-testid="hero-trust-row"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.9 }}
          className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] uppercase tracking-[0.18em] text-[#f5f5f0]/55"
        >
          <span className="inline-flex items-center gap-1.5"><Flag size={12} className="text-[#c5a059]" /> Made in USA</span>
          <span className="hidden sm:block h-3 w-px bg-white/15" />
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={12} className="text-[#c5a059]" /> Medical-grade formulas</span>
          <span className="hidden sm:block h-3 w-px bg-white/15" />
          <span className="inline-flex items-center gap-1.5"><Package size={12} className="text-[#c5a059]" /> Retail-ready</span>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0, scale: 0.88 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} className="hidden lg:flex flex-col items-center justify-center">
        <RoaringLogo className="w-[26rem] max-w-full h-auto drop-shadow-[0_0_45px_rgba(197,160,89,0.3)]" />
        <span className="mt-4 text-[11px] uppercase tracking-[0.3em] text-[#e8c877] drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)]">Tap the lion to roar</span>
        <button data-testid="lion-routine-desktop" onClick={openAdvisor} className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#c5a059] hover:text-[#d4af37] transition-colors">
          Ready to find your routine?
        </button>
      </motion.div>
    </div>
  </section>
);

/* ---------------- Roaring Logo ---------------- */
const RoaringLogo = ({ className, testId = "roaring-logo" }) => {
  const [phase, setPhase] = useState("idle");
  const growlRef = useRef(null);
  const roarRef = useRef(null);
  const timers = useRef([]);

  const roar = (sound = true) => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setPhase("idle");
    const g = growlRef.current;
    const r = roarRef.current;
    if (sound && g) { g.currentTime = 0; g.volume = 0.26; g.play().catch(() => {}); }
    requestAnimationFrame(() => setPhase("growl"));
    timers.current.push(setTimeout(() => {
      if (sound && r) { r.currentTime = 0; r.volume = 0.5; r.play().catch(() => {}); }
      setPhase("burst");
    }, 950));
    timers.current.push(setTimeout(() => setPhase("idle"), 1950));
  };

  useEffect(() => {
    const t = setTimeout(() => roar(false), 1400);
    return () => { clearTimeout(t); timers.current.forEach(clearTimeout); };
  }, []);

  return (
    <div
      className="relative inline-flex items-center justify-center cursor-pointer select-none"
      onMouseEnter={() => roar(false)}
      onClick={() => roar(true)}
      data-testid={testId}
      title="Roar!"
    >
      <audio ref={growlRef} src="/growl.mp3" preload="none" />
      <audio ref={roarRef} src="/roar.mp3" preload="none" />
      {phase === "burst" &&
        [0, 1, 2].map((i) => (
          <span
            key={i}
            className="absolute inset-0 rounded-full border-2 border-[#c5a059]/60 pointer-events-none"
            style={{ animation: `roar-ring 1s ease-out ${i * 0.16}s forwards` }}
          />
        ))}
      <motion.img
        src={LOGO_LG}
        alt="Royal King Skin Co. logo"
        className={className}
        animate={
          phase === "burst"
            ? { scale: [1, 1.15, 1.05, 1.12, 1], rotate: [0, -3, 3, -1.5, 0], filter: ["brightness(1)", "brightness(1.28)", "brightness(1.12)", "brightness(1.2)", "brightness(1)"] }
            : phase === "growl"
            ? { scale: [1, 1.035, 1.02, 1.04], rotate: [0, -1, 1, -0.5] }
            : { scale: 1, rotate: 0, filter: "brightness(1)" }
        }
        transition={{ duration: phase === "growl" ? 0.95 : 1, ease: "easeOut" }}
        draggable={false}
      />
    </div>
  );
};

/* ---------------- Marquee ---------------- */
const Marquee = () => {
  const words = ["Cleansers", "Moisturizers", "Mineral SPF", "Clay Mask", "Bundles", "Made in USA", "Clean Formulas"];
  const strip = [...words, ...words];
  return (
    <div className="bg-[#0a0a0a] py-5 overflow-hidden">
      <div className="flex whitespace-nowrap animate-marquee">
        {strip.map((w, i) => (
          <span key={i} className="flex items-center gap-24 pr-24 text-sm uppercase tracking-[0.25em] text-[#c5a059]/60">
            {w}<span className="text-[#c5a059]">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
};

/* ---------------- Origin Story ---------------- */
const OriginStory = () => (
  <section className="max-w-3xl mx-auto px-6 lg:px-10 py-24 lg:py-28 text-center">
    <Reveal>
      <p className="text-xs uppercase tracking-[0.35em] text-[#c5a059]">It Started With a Question</p>
      <div className="mt-8 space-y-5 font-serif text-xl sm:text-2xl leading-relaxed text-[#f5f5f0]/85 font-light">
        <p>
          I wasn't trying to sell skincare. I was simply testing products for myself when I went to my granddaughter's birthday party. The first person I spoke to asked,
          <span className="text-[#d4af37] italic"> “What are you wearing on your face?”</span>
        </p>
        <p>
          That question got me thinking: maybe men deserve a skincare brand built around them.
        </p>
      </div>
      <p className="mt-8 text-sm uppercase tracking-[0.25em] text-[#c5a059]">
        And that's how Royal King Skin Co. began.
      </p>
    </Reveal>
  </section>
);


/* ---------------- Start Here — The Method + Made in USA ---------------- */
const START_STEPS = [
  { n: "01", title: "Rise", note: "Wash away the day" },
  { n: "02", title: "Refine", note: "Smooth & polish" },
  { n: "03", title: "Restore", note: "Lock in moisture" },
  { n: "04", title: "Reign", note: "Shield with SPF" },
];

const StartHere = () => (
  <section id="start-here" className="relative overflow-hidden border-y border-[#c5a059]/25 bg-[#0a0a0a] py-24 lg:py-32">
    <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 50% 0%, #c5a059 0%, transparent 55%)" }} />
    <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
      <Reveal>
        <img loading="lazy" decoding="async" src={IMG.usaSeal} alt="Proudly Made in the USA" data-testid="usa-seal" className="mx-auto h-24 w-24 sm:h-28 sm:w-28 object-contain drop-shadow-[0_0_30px_rgba(197,160,89,0.35)]" />
        <p className="mt-6 text-xs uppercase tracking-[0.4em] text-[#c5a059]">The Throne Room · Proudly Made in the USA</p>
        <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-6xl tracking-tight text-[#f5f5f0]">
          Start Here. <span className="italic text-[#d4af37]">Yeah — that's it.</span>
        </h2>
        <p className="mt-5 max-w-2xl mx-auto text-[#f5f5f0]/82 font-light leading-relaxed text-lg">
          No hunting. No guessing. American-made essentials for the only steps that matter. Browse the full collection below.
        </p>
      </Reveal>

      <Reveal delay={0.08}>
        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-3xl mx-auto">
          {START_STEPS.map((s, i) => (
            <div key={s.title} data-testid={`start-step-${i}`} className="rounded-sm border border-[#c5a059]/25 bg-[#121212] px-5 py-7 text-center hover:border-[#c5a059]/60 hover:-translate-y-1 transition-all duration-300">
              <div className="font-serif text-3xl text-[#c5a059]">{s.n}</div>
              <div className="mt-2 font-serif text-lg text-[#f5f5f0]">{s.title}</div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.12em] text-[#f5f5f0]/55">{s.note}</div>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            data-testid="starter-shop-button"
            onClick={() => scrollTo("products")}
            className="inline-flex items-center justify-center gap-2 rounded-sm bg-[#c5a059] px-10 py-4 text-sm font-medium tracking-wide text-black hover:bg-[#d4af37] transition-colors"
          >
            <Crown size={18} /> Shop the Routine
          </button>
          <button
            data-testid="starter-advisor-button"
            onClick={openAdvisor}
            className="inline-flex items-center justify-center gap-2 rounded-sm border border-white/15 px-8 py-4 text-sm font-medium text-[#f5f5f0]/75 hover:border-[#c5a059] hover:text-[#c5a059] transition-colors"
          >
            <Sparkles size={16} /> Want it personalized?
          </button>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- The King's Code ---------------- */
const KINGS_CODE = [
  "Take care of yourself.",
  "Show up with confidence.",
  "Stay humble.",
  "Treat people right.",
  "Keep learning.",
];

const KingsCode = () => (
  <section className="border-y border-white/10 bg-[#0a0a0a] py-24 lg:py-32">
    <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
      <Reveal>
        <Crown size={28} className="mx-auto text-[#c5a059]" />
        <p className="mt-6 text-xs uppercase tracking-[0.35em] text-[#c5a059]">The King's Code</p>
        <p className="mt-4 max-w-lg mx-auto text-[#f5f5f0]/80 font-light">
          A king doesn't have to tell everyone he's a king. He just lives by a code.
        </p>
      </Reveal>
      <div className="mt-12 space-y-5">
        {KINGS_CODE.map((rule, i) => (
          <Reveal key={rule} delay={i * 0.06}>
            <p data-testid={`kings-code-${i}`} className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#f5f5f0]">
              {rule}
            </p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- The Royal King Routine ---------------- */
const ROUTINE_STEPS = [
  { n: "01", icon: Droplets, title: "Rise", desc: "Wash away dirt, oil and the day. A clean canvas is where every king starts." },
  { n: "02", icon: Sparkles, title: "Refine", desc: "Target your concern — brighten, smooth or firm — with a serum made for it." },
  { n: "03", icon: Leaf, title: "Restore", desc: "Lock in moisture so your skin stays comfortable, healthy and never tight." },
  { n: "04", icon: ShieldCheck, title: "Reign", desc: "Shield your skin from the sun and the day ahead. The final step, every time." },
];

const RoyalRoutine = () => (
  <section id="routine" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
    <Reveal className="text-center">
      <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">Look in the Mirror</p>
      <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#f5f5f0]">You've got places to go.</h2>
      <p className="mt-4 max-w-xl mx-auto text-[#f5f5f0]/80 font-light leading-relaxed">
        You don't need a 12-step routine. Give us two minutes a day and we'll give you a routine that works.
      </p>
    </Reveal>

    <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {ROUTINE_STEPS.map((s, i) => (
        <Reveal key={s.title} delay={i * 0.08}>
          <div data-testid={`routine-step-${s.title.toLowerCase()}`} className="h-full rounded-sm border border-white/10 bg-[#121212] p-7 hover:border-[#c5a059]/40 transition-colors">
            <div className="flex items-center justify-between">
              <s.icon size={24} className="text-[#c5a059]" />
              <span className="font-serif text-2xl text-[#c5a059]/30">{s.n}</span>
            </div>
            <h3 className="mt-5 font-serif text-xl text-[#f5f5f0]">{s.title}</h3>
            <p className="mt-2 text-[15px] text-[#f5f5f0]/80 font-light leading-relaxed">{s.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>

    <Reveal delay={0.1}>
      <div className="mt-14 rounded-sm border border-[#c5a059]/25 bg-[#0d0d0d] p-8 lg:p-12 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">Don't Have Time for Skincare?</p>
        <h3 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#f5f5f0]">Give us two minutes.</h3>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
          {ROUTINE_STEPS.map((s, i) => (
            <div key={s.title} className="flex items-center gap-3 sm:gap-4">
              <span className="inline-flex items-center gap-2 rounded-sm border border-white/10 px-4 py-2 text-sm text-[#f5f5f0]/80">
                <span className="text-[#c5a059] font-serif">{s.n}</span> {s.title}
              </span>
              {i < ROUTINE_STEPS.length - 1 && <ArrowRight size={16} className="text-[#c5a059]/50" />}
            </div>
          ))}
        </div>
        <p className="mt-8 text-[#f5f5f0]/80 font-light leading-relaxed max-w-lg mx-auto">
          You don't need a shelf full of bottles. You need a routine you'll actually stick with.
        </p>
        <button
          data-testid="routine-find-button"
          onClick={openAdvisor}
          className="mt-8 inline-flex items-center justify-center gap-2 rounded-sm bg-[#c5a059] px-10 py-4 text-sm font-medium tracking-wide text-black hover:bg-[#d4af37] transition-colors"
        >
          <Sparkles size={18} /> Find My Royal Routine
        </button>
      </div>
    </Reveal>
  </section>
);

/* ---------------- Products ---------------- */
const FEEL = {
  "Moisturizing Eye Cream (1.7 oz)": "Rich Cream · Fast-Absorbing · Fragrance-Free",
  "Hydra Toner (4 oz)": "Light Mist · Refreshing · Cucumber & Aloe",
  "24K Gold Under Eye Patch (60 Count)": "Cooling Gel · Depuffing · Fragrance-Free",
  "Microdermabrasion Scrub": "Gritty Polish · Smoothing · Fresh & Clean",
  "Exfoliation Scrub with Kojic Acid (1.7 oz)": "Fine Grit · Brightening · Citrus",
  "Blue Clay Exfoliation Mask (1.7 oz)": "Clay Mask · Exfoliating · Brightening",
  "24K Gold Mask — Full Mask": "Hydrogel Sheet · Firming · Fragrance-Free",
  "24K Gold Mask — Half Mask": "Hydrogel Sheet · Firming · Fragrance-Free",
  "Mineral SPF 50 (1.7 oz)": "Broad Spectrum · Mineral Filters · Zinc + Titanium",
  "Ingrown Hair Serum (1.7 oz)": "Targeted Care · Salicylic + Glycolic · Tea Tree & Aloe",
  "Oil-Free Moisturizer (1.7 oz)": "Oil-Free · Lightweight · Hyaluronic + Niacinamide",
  "DNA Moisturizer (1.7 oz)": "Rich Cream · Nourishing · Sandalwood & Cedar",
  "Eye Lifting Cream (0.5 oz)": "Silky Cream · Firming · Fragrance-Free",
  "HA + Zinc Serum": "Lightweight Serum · Fast-Absorbing · Unscented",
  "Vitamin C Boost (1 oz)": "Silky Serum · Brightening · Citrus",
  "Vitamin C + E Serum with Ferulic Acid": "Silky Serum · Brightening · Light Citrus",
  "Creamy Cleansing Balm (4 oz / 120 mL)": "Balm-to-Oil · Hydrating · Shea & Jojoba",
  "Fresh Reign Glycolic Cleanser": "Gel Lather · Clarifying · Fresh & Clean",
  "Anti-Aging Pads + TCA (50 Count)": "Soaked Pads · Resurfacing · Light Citrus",
  "Royal Reserve": "The Complete Regimen · 8 Essentials · Rise to Reign",
  "The Polished Prince": "Rise · Restore · Reign · 3 Steps",
  "The Majestic Mane": "Rise · Refine · Restore · Reign · 4 Steps",
  "The Regal Glow": "Rise · Refine · Restore · 3 Steps",
};

/* Two signature routines — Clean-Shaven & Bearded */
const CLEAN_SHAVEN_BUNDLE = {
  name: "The Polished Prince",
  price: 199,
  compareAt: 235,
  save: 36,
  pct: 15,
  tag: "Routine",
  featured: true,
  offerLine: "First 100 opening day — 15% off any add-on",
  desc: "For the man who keeps it clean and sharp. Our Creamy Cleansing Balm (4 oz) melts away dirt and oil, the Oil-Free Moisturizer hydrates with no greasy shine, and Mineral SPF 50 helps protect against UVA and UVB rays. Clean, polished, and ready to rule.",
  img: IMG.cleanBundle,
};
const BEARDED_BUNDLE = {
  name: "The Majestic Mane",
  price: 275,
  compareAt: 323,
  save: 48,
  pct: 15,
  freeShip: true,
  offerLine: "First 100 opening day — 15% off any add-on",
  tag: "Routine",
  featured: true,
  desc: "Built for the bearded king. Our Creamy Cleansing Balm (4 oz) gently cleans skin and beard, the Blue Clay Exfoliation Mask helps exfoliate and brighten, the Oil-Free Moisturizer hydrates with no shine, and Mineral SPF 50 helps protect against UVA and UVB rays. Strength you can see, confidence you can feel.",
  img: IMG.beardedBundle,
};
const SMOOTH_BUNDLE = {
  name: "The Regal Glow",
  price: 188,
  compareAt: 221,
  save: 33,
  pct: 15,
  offerLine: "First 100 opening day — 15% off any add-on",
  tag: "Routine",
  featured: true,
  desc: "Smooth skin, royal finish. Our Creamy Cleansing Balm (4 oz) melts away dirt and oil, the Ingrown Hair Serum soothes and helps smooth the look of razor bumps, and the Oil-Free Moisturizer hydrates with no greasy shine. Smooth, even, and glowing like true royalty.",
  img: "https://static.prod-images.emergentagent.com/jobs/93049cf5-79bf-44c6-8d5b-f3eaad7ea344/images/a4a1778b3bb07ef1b78bfa67dff04142e18e939ec9870bbcc8030b42a3756fd7.jpeg",
};
const withSku = (name, sku) => ({ ...PRODUCTS.find((p) => p.name === name), sku });
const SHOP_PRODUCTS = [
  { ...CLEAN_SHAVEN_BUNDLE, sku: "bundle_clean_shaven" },
  { ...BEARDED_BUNDLE, sku: "bundle_bearded" },
  { ...SMOOTH_BUNDLE, sku: "bundle_smooth" },
  withSku("Creamy Cleansing Balm (4 oz / 120 mL)", "creamy_balm"),
  withSku("Oil-Free Moisturizer (1.7 oz)", "oil_free_moisturizer"),
  withSku("Mineral SPF 50 (1.7 oz)", "mineral_spf50"),
  withSku("Blue Clay Exfoliation Mask (1.7 oz)", "blue_clay_mask"),
  withSku("Ingrown Hair Serum (1.7 oz)", "ingrown_serum"),
];


/* ---------------- Grand Opening Offer ---------------- */
const OFFER_STEPS = [
  { n: "01", icon: Crown, title: "Join the Royal List", note: "Be one of the first 100 kings in line." },
  { n: "02", icon: Sparkles, title: "Shop a Bundle on Opening Day", note: "Any royal routine — Polished Prince, Majestic Mane or Regal Glow." },
  { n: "03", icon: Gift, title: "Take 15% Off Any Add-On", note: "Stack any single item onto your order." },
];

const GrandOpeningOffer = () => (
  <section id="offer" className="scroll-mt-28 relative overflow-hidden bg-gradient-to-b from-[#0a0a0a] via-[#141007] to-[#0a0a0a] border-y-2 border-[#c5a059]/40 py-20 lg:py-28">
    <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 50% 10%, #c5a059 0%, transparent 60%)" }} />
    <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
      <Reveal>
        <span data-testid="offer-badge" className="inline-flex items-center gap-2 rounded-full border border-[#c5a059] bg-[#c5a059]/10 px-4 py-1.5 text-[11px] uppercase tracking-[0.3em] text-[#c5a059] font-semibold">
          <Gift size={13} /> Grand Opening Offer
        </span>
        <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#f5f5f0]">
          First <span className="text-[#d4af37]">100 Kings</span>, Opening Day
        </h2>
        <p className="mt-4 mx-auto max-w-2xl text-lg text-[#f5f5f0]/85 font-light leading-relaxed">
          Buy any royal routine and take <span className="text-[#d4af37] font-medium">15% off any add-on item</span> of your choice. First come, first crowned — this one's for the first 100 only.
        </p>
      </Reveal>

      <Reveal delay={0.08}>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {OFFER_STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.n} data-testid={`offer-step-${i}`} className="rounded-sm border border-[#c5a059]/25 bg-[#0d0d0d]/80 p-6 text-left hover:border-[#c5a059]/60 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-sm border border-[#c5a059]/40 bg-[#c5a059]/10 text-[#c5a059]"><Icon size={18} /></span>
                  <span className="font-serif text-2xl text-[#c5a059]/35">{s.n}</span>
                </div>
                <h3 className="mt-4 font-serif text-lg text-[#f5f5f0]">{s.title}</h3>
                <p className="mt-1.5 text-sm text-[#f5f5f0]/75 font-light leading-relaxed">{s.note}</p>
              </div>
            );
          })}
        </div>
      </Reveal>

      <Reveal delay={0.12}>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] uppercase tracking-[0.15em] text-[#f5f5f0]/65">
          <span className="inline-flex items-center gap-1.5"><Truck size={13} className="text-[#c5a059]" /> Free shipping on the Majestic Mane</span>
        </div>
        <button
          data-testid="offer-cta"
          onClick={() => scrollTo("waitlist")}
          className="mt-9 inline-flex items-center justify-center gap-2 rounded-sm bg-[#c5a059] px-10 py-4 text-sm font-medium tracking-wide text-black hover:bg-[#d4af37] transition-colors"
        >
          <Crown size={18} /> Join the Royal List — Be First
        </button>
        <p className="mt-4 text-xs text-[#f5f5f0]/45 font-light">Offer valid opening day for the first 100 orders. One add-on discount per order.</p>
      </Reveal>
    </div>
  </section>
);


const usePhoto = (p) => {
  const { photos } = useStore();
  return photos[p.sku] ? `${process.env.REACT_APP_BACKEND_URL}${photos[p.sku]}` : p.img;
};

const ProductImg = ({ p }) => (
  <img loading="lazy" decoding="async" src={usePhoto(p)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms] ease-out" />
);

const useVisible = () => {
  const { launch, vip } = useStore();
  return (p) => vip || !(launch.vip_only || []).includes(p.sku);
};

const ProductAction = ({ p, i }) => {
  const { storeOpen, add, setOpen, stock } = useStore();
  const img = usePhoto(p);
  if (stock[p.sku] === "out") {
    return <button data-testid={`product-soldout-${i}`} disabled className="mt-5 w-full rounded-sm border border-white/15 py-3 text-sm text-[#f5f5f0]/50">Sold Out</button>;
  }
  const buy = () => {
    add({ ...p, img });
    if (BUNDLE_SKUS.includes(p.sku)) goldConfetti();
    toast.success(`${p.name} ${storeOpen ? "added to your order" : "pre-ordered to your cart"}`, { action: { label: "View Cart", onClick: () => setOpen(true) } });
  };
  return (
    <div>
      <button data-testid={storeOpen ? `product-add-${i}` : `product-preorder-${i}`} onClick={buy} className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-sm bg-[#c5a059] py-3 text-sm font-semibold text-black hover:bg-[#d4af37] transition-colors">
        <Crown size={15} /> {storeOpen ? "Add to Cart" : "Pre-order Now"}
      </button>
      {!storeOpen && <p data-testid={`product-preorder-note-${i}`} className="mt-2 text-center text-[11px] uppercase tracking-[0.2em] text-[#c5a059]/80">Ships in 7–10 days</p>}
    </div>
  );
};

const CROWNS = [
  { label: "The Polished Prince", price: "$199", was: "$235", save: "15%", key: "clean", step: "Rise", product: "Creamy Cleansing Balm", cues: [[0, "Start with a clean palm"], [3.2, "Scoop a small amount"], [6, "Cover your whole face"], [9.5, "Massage in slow circles"], [14, "Rinse with warm water"], [19.5, "Pat dry with a soft towel"], [23, "Clean. Fresh. Ready to rule."]], video: "/prince-ritual", img: "/prince-start.jpg" },
  { label: "The Majestic Mane", price: "$275", was: "$323", save: "15%", key: "bearded", step: "Reset", product: "Blue Clay Exfoliation Mask", cues: [[0, "Your weekly reset"], [4.5, "Scoop a little clay"], [8.5, "Spread a thin, even layer"], [14, "Let the clay do its work"], [16.3, "Rinse with warm water"], [21, "Pat dry with a soft towel"], [23.4, "Clean. Fresh. Ready to rule."]], video: "/mane-ritual", img: "/mane-mask-start.jpg" },
  { label: "The Regal Glow", price: "$188", was: "$221", save: "15%", key: "smooth", step: "Refine", product: "Ingrown Hair Serum", cues: [[0, "Right after you shave"], [3.5, "Three drops into your palm"], [7.5, "Warm it between your fingertips"], [10, "Smooth along both sides of the jaw"], [13, "Kings don't bump. They glide."]], video: "/glow-serum-web", img: "/glow-serum-start.jpg", silent: true },
];

const CHOOSER_STORIES = {
  clean: "The Sharp Sovereign starts every day clean, polished and protected. No shine, no shortcuts.",
  bearded: "The Duke's Mane keeps his beard soft and his skin clear beneath it.",
  smooth: "The Royal Remedy shaves close and never pays for it. No bumps, no burn, just a smooth finish.",
};

const CHOOSER_NAMES = { clean: "The Sharp Sovereign", bearded: "The Duke's Mane", smooth: "The Royal Remedy" };

const CHOOSER_FILMS = { clean: "present-samoan", bearded: "present-sikh", smooth: "present-latino" };

const useAutoPlay = (ref, threshold) => {
  useEffect(() => {
    const v = ref.current;
    let seen = false;
    const io = new IntersectionObserver(([e]) => { seen = e.isIntersecting; seen && !window.__rkFeature ? v.play().catch(() => {}) : v.pause(); }, { threshold });
    const feature = (e) => (e.detail ? v.pause() : seen && v.play().catch(() => {}));
    io.observe(v);
    window.addEventListener("rk-feature", feature);
    return () => { io.disconnect(); window.removeEventListener("rk-feature", feature); };
  }, [ref, threshold]);
};

const ChooserFilm = ({ k, alt, i, turn, setTurn }) => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  const [feat, setFeat] = useState(false);
  const wide = typeof window !== "undefined" && window.matchMedia("(min-width: 640px)").matches;
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setSeen(e.isIntersecting), { threshold: 0.3 });
    io.observe(ref.current);
    const onFeat = (e) => setFeat(!!e.detail);
    window.addEventListener("rk-feature", onFeat);
    return () => { io.disconnect(); window.removeEventListener("rk-feature", onFeat); };
  }, []);
  useEffect(() => {
    const v = ref.current;
    if (seen && !feat && (!wide || turn === i)) v.play().catch(() => {});
    else { v.pause(); if (wide) v.currentTime = 0; }
  }, [seen, feat, turn, i, wide]);
  const f = CHOOSER_FILMS[k];
  return (
    <video ref={ref} data-testid={`king-chooser-film-${k}`} poster={`/${f}.jpg`} aria-label={alt} muted playsInline loop={!wide} preload="metadata"
      onEnded={() => setTurn((i + 1) % 3)}
      className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105">
      <source src={`/${f}.mp4`} type="video/mp4" />
      <source src={`/${f}.webm`} type="video/webm" />
    </video>
  );
};

const RitualCaption = ({ vref, cues, k }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const v = vref.current;
    const on = () => setIdx(cues.reduce((n, [t], j) => (v.currentTime >= t ? j : n), 0));
    v.addEventListener("timeupdate", on);
    return () => v.removeEventListener("timeupdate", on);
  }, [vref, cues]);
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-5 pb-5 pt-16">
      <p key={idx} data-testid={`ritual-caption-${k}`} className="ritual-cap font-serif italic text-lg sm:text-xl text-[#d4af37] drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]">{cues[idx][1]}</p>
    </div>
  );
};

const useVoiceToggle = (ref, key) => {
  const [voice, setVoice] = useState(false);
  useEffect(() => {
    const v = ref.current;
    const other = (e) => { if (e.detail !== key) { v.muted = true; setVoice(false); } };
    const feature = (e) => { if (e.detail) { v.muted = true; setVoice(false); } };
    window.addEventListener("rk-ritual-voice", other);
    window.addEventListener("rk-feature", feature);
    return () => { window.removeEventListener("rk-ritual-voice", other); window.removeEventListener("rk-feature", feature); };
  }, [ref, key]);
  const toggleVoice = () => {
    const v = ref.current;
    const next = !voice;
    if (next) {
      document.querySelectorAll("video[data-feature]").forEach((f) => f.pause());
      window.dispatchEvent(new CustomEvent("rk-ritual-voice", { detail: key }));
      window.dispatchEvent(new CustomEvent("rk-spa", { detail: true }));
      v.muted = false;
      v.currentTime = 0;
      const go = () => v.play().catch(() => { v.muted = true; setVoice(false); });
      v.play().catch(() => v.addEventListener("seeked", go, { once: true }));
    } else { window.dispatchEvent(new CustomEvent("rk-spa", { detail: false })); v.muted = true; }
    setVoice(next);
  };
  return [voice, toggleVoice];
};

const VoiceButton = ({ voice, onClick, testid, className = "top-4 right-4" }) => (
  <button data-testid={testid} onClick={onClick} aria-pressed={voice}
    className={`absolute ${className} z-10 inline-flex items-center gap-2 whitespace-nowrap rounded-full border px-3.5 py-2 text-[10px] uppercase tracking-[0.25em] backdrop-blur-md transition-colors duration-300 ${voice ? "border-[#c5a059] bg-[#c5a059]/20 text-[#d4af37]" : "border-white/25 bg-black/45 text-[#f5f5f0]/85 hover:border-[#c5a059]/70 hover:text-[#d4af37]"}`}>
    {voice ? <Volume2 size={13} /> : <VolumeX size={13} />}
    {voice ? "Sound On" : "Tap for Sound"}
  </button>
);

const RitualFilm = ({ r, i }) => {
  const ref = useRef(null);
  useAutoPlay(ref, 0.4);
  const [voice, toggleVoice] = useVoiceToggle(ref, r.key);
  return (
    <Reveal delay={i * 0.12}>
      <figure data-testid={`ritual-film-${r.key}`} className="group">
        <div className="relative aspect-[2/3] overflow-hidden rounded-sm border border-[#c5a059]/25">
          {!r.silent && <VoiceButton voice={voice} onClick={toggleVoice} testid={`ritual-voice-${r.key}`} />}
          <video ref={ref} poster={r.img} muted playsInline loop preload="metadata" aria-label={`${r.label} ritual`} className="h-full w-full object-cover transition-transform duration-[1500ms] group-hover:scale-[1.03]">
            <source src={`${r.video}.webm`} type="video/webm" />
            <source src={`${r.video}.mp4`} type="video/mp4" />
          </video>
          <span className="absolute top-4 left-4 font-serif text-5xl text-[#c5a059]/80">0{i + 1}</span>
          <RitualCaption vref={ref} cues={r.cues} k={r.key} />
        </div>
        <figcaption className="mt-5">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#c5a059]">{r.step}</p>
          <h3 className="mt-2 font-serif text-2xl text-[#f5f5f0]">{r.product}</h3>
          <p className="mt-1 text-sm text-[#f5f5f0]/60">Featured in {r.label}</p>
          <ShareRitual r={r} />
        </figcaption>
      </figure>
    </Reveal>
  );
};

const SpaSound = () => {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const a = new Audio("/spa-ritual.mp3");
    a.preload = "none";
    a.loop = true;
    a.volume = 0;
    ref.current = a;
    const sec = document.getElementById("ritual");
    const io = new IntersectionObserver(([e]) => { if (!e.isIntersecting && !a.paused) { a.pause(); a.volume = 0; setOn(false); window.dispatchEvent(new CustomEvent("rk-spa", { detail: false })); } }, { threshold: 0 });
    if (sec) io.observe(sec);
    return () => { io.disconnect(); a.pause(); };
  }, []);
  const fade = (to) => {
    const a = ref.current;
    const step = () => { a.volume = Math.max(0, Math.min(0.4, a.volume + (to > a.volume ? 0.02 : -0.04))); if (Math.abs(a.volume - to) > 0.03) requestAnimationFrame(step); else { a.volume = to; if (!to) a.pause(); } };
    step();
  };
  const toggle = () => {
    const next = !on;
    window.dispatchEvent(new CustomEvent("rk-spa", { detail: next }));
    if (next) { ref.current.play().catch(() => {}); fade(0.4); } else fade(0);
    setOn(next);
  };
  return (
    <button data-testid="spa-sound-toggle" onClick={toggle} aria-pressed={on}
      className={`mt-8 inline-flex items-center gap-3 rounded-full border px-5 py-2.5 text-xs uppercase tracking-[0.25em] transition-colors duration-300 ${on ? "border-[#c5a059] bg-[#c5a059]/10 text-[#c5a059]" : "border-white/20 text-[#f5f5f0]/75 hover:border-[#c5a059]/60 hover:text-[#c5a059]"}`}>
      {on ? <Volume2 size={15} /> : <VolumeX size={15} />}
      {on ? "Ritual Jazz On" : "Play Ritual Jazz"}
      {on && <span className="flex items-end gap-[2px] h-3">{[0, 1, 2].map((i) => <span key={i} className="w-[2px] bg-[#c5a059] animate-pulse" style={{ height: `${6 + i * 3}px`, animationDelay: `${i * 0.2}s` }} />)}</span>}
    </button>
  );
};

const ShareRitual = ({ r }) => {
  const fileRef = useRef(null);
  const warm = () => {
    if (fileRef.current) return;
    fileRef.current = "loading";
    fetch(`${r.video}.mp4`).then((x) => x.blob()).then((b) => { fileRef.current = new File([b], `royal-ritual-${r.key}.mp4`, { type: "video/mp4" }); }).catch(() => { fileRef.current = null; });
  };
  const url = `${window.location.origin}/#ritual`;
  const text = `The Royal Ritual: ${r.product} by Royal King Skin Co. Step in as a man, walk out a King.`;
  const share = async () => {
    try {
      const file = fileRef.current instanceof File ? fileRef.current : null;
      if (file && navigator.canShare?.({ files: [file] })) return await navigator.share({ files: [file], title: "The Royal Ritual", text: `${text} ${url}` });
      if (navigator.share) return await navigator.share({ title: "The Royal Ritual", text, url });
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
    await navigator.clipboard?.writeText(`${text} ${url}`).catch(() => {});
    toast.success("Link copied, ready to paste anywhere");
  };
  const sms = `sms:?&body=${encodeURIComponent(`${text} ${url}`)}`;
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      <button data-testid={`ritual-share-${r.key}`} onPointerEnter={warm} onPointerDown={warm} onClick={share} className="inline-flex items-center gap-2 rounded-full border border-[#c5a059]/45 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-[#c5a059] hover:bg-[#c5a059] hover:text-black transition-colors duration-300">
        <Share2 size={13} /> Share to Instagram
      </button>
      <a data-testid={`ritual-text-${r.key}`} href={sms} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-[#f5f5f0]/75 hover:border-[#c5a059]/60 hover:text-[#c5a059] transition-colors duration-300">
        <MessageCircle size={13} /> Text a Friend
      </a>
    </div>
  );
};

const IG_HANDLE = "j_dantecur";
const IG_URL = `https://www.instagram.com/${IG_HANDLE}/`;

const RitualReel = () => {
  const ref = useRef(null);
  useAutoPlay(ref, 0.4);
  const [voice, toggleVoice] = useVoiceToggle(ref, "reel");
  return (
    <Reveal>
      <div data-testid="ritual-reel" className="mt-24 grid items-center gap-12 lg:grid-cols-[auto_1fr] lg:gap-20">
        <div className="relative mx-auto w-[260px] sm:w-[290px] rounded-[2.75rem] border border-[#c5a059]/40 bg-[#111] p-3 shadow-[0_40px_90px_-30px_rgba(197,160,89,0.35)]">
          <div className="absolute left-1/2 top-5 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-black" />
          <VoiceButton voice={voice} onClick={toggleVoice} testid="ritual-reel-voice" className="bottom-8 left-1/2 -translate-x-1/2" />
          <video ref={ref} data-testid="ritual-reel-video" poster="/reel-poster.jpg" muted playsInline loop preload="metadata" aria-label="The Royal Ritual reel" className="aspect-[9/16] w-full rounded-[2.2rem] object-cover bg-black">
            <source src="/royal-ritual-reel-web.webm" type="video/webm" />
            <source src="/royal-ritual-reel-web.mp4" type="video/mp4" />
          </video>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.35em] text-[#c5a059]">Made for Your Feed</p>
          <h3 className="mt-4 font-serif text-3xl sm:text-4xl text-[#f5f5f0]">The whole ritual in 39 seconds.</h3>
          <p className="mt-4 max-w-md text-[#f5f5f0]/65 font-light leading-relaxed">Rise, refine, restore: three kings, one routine. Send it to a man who deserves his crown.</p>
          <ShareRitual r={{ key: "reel", video: "/royal-ritual-reel-web", product: "the full ritual" }} />
          <a data-testid="ritual-reel-instagram" href={IG_URL} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 text-sm text-[#f5f5f0]/70 hover:text-[#c5a059] transition-colors duration-300">
            <Instagram size={16} className="text-[#c5a059]" /> Follow the ritual <span className="text-[#c5a059]">@{IG_HANDLE}</span>
          </a>
        </div>
      </div>
    </Reveal>
  );
};

const RoyalRitual = () => (
  <section id="ritual" data-testid="royal-ritual" className="relative bg-[#070707] border-y border-[#c5a059]/15 py-24 lg:py-32">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(197,160,89,0.10),transparent_60%)] pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="text-[11px] uppercase tracking-[0.35em] text-[#c5a059]">Now Showing</p>
        <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#f5f5f0]">The Royal Ritual</h2>
        <p className="mt-5 max-w-xl text-[#f5f5f0]/70 font-light leading-relaxed">Three kings. Three minutes. See how every routine comes to life — the way it feels in your hands, every single morning.</p>
        <SpaSound />
      </Reveal>
      <Reveal>
        <div data-testid="ritual-spiel" className="mt-16 grid items-center gap-10 rounded-sm border border-[#c5a059]/25 bg-[#0b0a08] p-6 sm:p-10 lg:grid-cols-[minmax(0,380px)_1fr] lg:gap-16">
          <FounderVideo src="/founder-story.mp4" title="Dante's Story" testid="ritual-spiel-video" />
          <div>
            <p className="text-[11px] uppercase tracking-[0.35em] text-[#c5a059]">Before the Ritual</p>
            <h3 className="mt-4 font-serif text-3xl sm:text-4xl text-[#f5f5f0]">A word from our founder.</h3>
            <p className="mt-5 max-w-lg font-serif italic text-xl leading-relaxed text-[#d4af37]">"Every man is born to be a king. Some of us just need the right routine."</p>
            <p className="mt-6 text-sm text-[#f5f5f0]/60">Dante Currington · Founder &amp; CEO, Royal King Skin Co.</p>
          </div>
        </div>
      </Reveal>
      <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-8 lg:gap-10">
        {CROWNS.map((r, i) => <RitualFilm key={r.key} r={r} i={i} />)}
      </div>
      <RitualReel />
      <Reveal>
        <button data-testid="ritual-shop-button" onClick={() => scrollTo("products")} className="mt-14 inline-flex items-center gap-2 rounded-sm bg-[#c5a059] px-8 py-4 text-sm font-semibold text-black hover:bg-[#d4af37] transition-colors">Choose Your Ritual <ArrowRight size={16} /></button>
      </Reveal>
    </div>
  </section>
);

const Products = () => {
  const [turn, setTurn] = useState(0);
  return (
  <section id="products" className="scroll-mt-48 sm:scroll-mt-28 max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
    <Reveal>
      <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">The Royal Collection</p>
      <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#f5f5f0]">Find Your King Routine</h2>
      <p className="mt-4 max-w-xl text-[#f5f5f0]/80 font-light leading-relaxed">
        Three signature routines — polished, bearded, or bump-free smooth. Pick your crown, or build your own from the essentials below.
      </p>
    </Reveal>

    <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6" data-testid="king-chooser">
      {CROWNS.map((r, i) => (
        <Reveal key={r.key} delay={i * 0.1}>
          <button data-testid={`king-chooser-${r.key}`} onClick={() => scrollTo("products")}
            className="group flex w-full flex-col overflow-hidden rounded-sm border border-[#c5a059]/30 bg-[#0d0d0d] text-left hover:border-[#c5a059]/70 transition-colors">
            <div className="relative aspect-[2/3] overflow-hidden">
              <ChooserFilm k={r.key} alt={`${CHOOSER_NAMES[r.key]} presenting ${r.label}`} i={i} turn={turn} setTurn={setTurn} />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#0d0d0d] to-transparent" />
            </div>
            <div className="px-6 pb-6 pt-3">
              <h3 data-testid={`king-chooser-name-${r.key}`} className="font-serif text-2xl text-[#f5f5f0]">{CHOOSER_NAMES[r.key]}</h3>
              <p data-testid={`king-chooser-bundle-${r.key}`} className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[#c5a059]">Wears {r.label}</p>
              <p data-testid={`king-chooser-story-${r.key}`} className="mt-3 font-serif italic text-[15px] leading-relaxed text-[#f5f5f0]/70">{CHOOSER_STORIES[r.key]}</p>
              <p className="mt-2 text-sm text-[#f5f5f0]/85">
                <span className="text-[#f5f5f0]/55 line-through mr-2">{r.was}</span>{r.price}<span className="ml-2 text-[#c5a059]">Save {r.save}</span>
              </p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[#c5a059]">Shop this routine <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></span>
            </div>
          </button>
        </Reveal>
      ))}
    </div>

    <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:[&>*:last-child:nth-child(3n+1)]:col-start-2">
      {SHOP_PRODUCTS.filter(useVisible()).map((p, i) => (
        <Reveal key={p.name} delay={(i % 3) * 0.08}>
          <div data-testid={`product-card-${i}`} className="group flex flex-col h-full rounded-sm border border-white/5 bg-[#121212] overflow-hidden hover:border-[#c5a059]/50 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_-20px_rgba(197,160,89,0.4)] transition-all duration-500">
            <div className="relative h-72 overflow-hidden bg-black">
              <ProductImg p={p} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <span className="absolute top-4 left-4 rounded-sm bg-black/70 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-wider text-[#c5a059] border border-[#c5a059]/30">{p.tag}</span>
              {p.comingSoon && (
                <span className="absolute top-4 right-4 rounded-sm bg-[#c5a059] px-3 py-1 text-[10px] uppercase tracking-wider text-black font-semibold">Coming Soon</span>
              )}
              {p.featured && (
                <span className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-sm bg-[#c5a059] px-3 py-1 text-[10px] uppercase tracking-wider text-black font-bold"><Crown size={11} /> Start Here</span>
              )}
              {p.addon && (
                <span className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-sm bg-black/70 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-wider text-[#c5a059] border border-[#c5a059]/40"><Sparkles size={11} /> {p.addon}</span>
              )}
              <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-[#c5a059]/0 group-hover:ring-[#c5a059]/40 transition-[box-shadow] duration-500" />
            </div>
            <div className="flex flex-col flex-1 p-6">
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#c5a059]/95">Royal King Skin Co.</span>
              <div className="mt-1.5 flex items-start justify-between gap-3">
                <h3 className="font-serif text-xl text-[#f5f5f0]">{p.name}</h3>
                <span className="flex flex-col items-end shrink-0">
                  {p.compareAt && (
                    <span data-testid={`product-compare-${i}`} className="text-sm text-[#f5f5f0]/75 line-through">
                      {`$${Number.isInteger(p.compareAt) ? p.compareAt : p.compareAt.toFixed(2)}`}
                    </span>
                  )}
                  <span className="font-serif text-lg text-[#c5a059]">
                    {p.price ? `$${Number.isInteger(p.price) ? p.price : p.price.toFixed(2)}` : <span className="text-sm text-[#f5f5f0]/55">Price soon</span>}
                  </span>
                </span>
              </div>
              <ProductMeta p={p} i={i} />
              {p.save && (
                <span data-testid={`product-save-${i}`} className="mt-1 inline-block self-start rounded-sm bg-[#c5a059]/15 border border-[#c5a059]/30 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#c5a059] font-semibold">
                  Save {p.pct ? `${p.pct}%` : `$${p.save}`}
                </span>
              )}
              {p.bonus && (
                <span data-testid={`product-bonus-${i}`} className="mt-1 ml-2 inline-flex items-center gap-1 self-start rounded-sm bg-[#c5a059] px-2 py-0.5 text-[10px] uppercase tracking-wider text-black font-bold">
                  <Sparkles size={11} /> Bonus · {p.bonus}
                </span>
              )}
              {p.freeShip && (
                <span data-testid={`product-freeship-${i}`} className="mt-1 ml-2 inline-flex items-center gap-1 self-start rounded-sm bg-[#c5a059]/15 border border-[#c5a059]/30 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#c5a059] font-semibold">
                  <Truck size={11} /> Free Shipping
                </span>
              )}
              {FEEL[p.name] && (
                <p data-testid={`product-feel-${i}`} className="mt-2 text-[13px] uppercase tracking-[0.12em] text-[#c5a059]/95 font-light">{FEEL[p.name]}</p>
              )}
              {p.giftTeaser && (
                <div data-testid={`product-gift-${i}`} className="mt-3 flex items-center gap-2 rounded-sm border border-[#c5a059] bg-[#c5a059]/10 px-3 py-2">
                  <Gift size={15} className="text-[#c5a059] shrink-0" />
                  <span className="text-[11px] sm:text-xs uppercase tracking-[0.1em] text-[#c5a059] font-bold leading-tight">{p.giftTeaser}</span>
                </div>
              )}
              {p.offerLine && (
                <p data-testid={`product-offer-${i}`} className="mt-2 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] text-[#d4af37] font-semibold">
                  <Sparkles size={12} className="shrink-0" /> {p.offerLine}
                </p>
              )}
              <p className="mt-2 text-base text-[#f5f5f0]/90 font-light leading-relaxed flex-1">{p.desc}</p>
              {p.comingSoon ? (
                <span data-testid={`product-soon-${i}`} className="mt-5 w-full text-center rounded-sm border border-white/15 py-3 text-sm font-medium text-[#f5f5f0]/85">
                  Coming Soon
                </span>
              ) : (
                <ProductAction p={p} i={i} />
              )}
              <div className="mt-3 flex justify-center gap-5 text-[11px] uppercase tracking-[0.15em] text-[#f5f5f0]/60">
                <button data-testid={`product-ingredients-link-${i}`} onClick={() => scrollTo("inside")} className="hover:text-[#c5a059] transition-colors">Key Ingredients</button>
                <span className="text-[#c5a059]/40">·</span>
                <button data-testid={`product-shipping-link-${i}`} onClick={() => scrollTo("shipping")} className="hover:text-[#c5a059] transition-colors">Shipping Info</button>
              </div>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
  );
};

/* ---------------- Ingredients / Benefits ---------------- */
const Ingredients = () => (
  <section id="ingredients" className="relative border-y border-white/10 bg-[#0d0d0d] py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <div className="relative h-[440px] overflow-hidden rounded-sm border border-white/10">
          <img loading="lazy" decoding="async" src={IMG.manCream} alt="Man applying Royal King Skin Co. moisturizer" className="w-full h-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/70 to-transparent" />
        </div>
      </Reveal>
      <div>
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">Inside the Crown · Why Royal King</p>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#f5f5f0]">Formulated to Reign</h2>
          <p className="mt-4 text-[#f5f5f0]/80 font-light leading-relaxed max-w-md">
            Powered by medical-grade actives and carefully selected, skin-friendly ingredients — nothing harsh, nothing hidden.
          </p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <Reveal key={b.title} delay={i * 0.08}>
                <div className="p-6 rounded-sm border border-white/10 bg-[#121212] hover:border-[#c5a059]/40 transition-colors h-full">
                  <Icon size={24} className="text-[#c5a059]" />
                  <h3 className="mt-4 font-serif text-lg text-[#f5f5f0]">{b.title}</h3>
                  <p className="mt-1.5 text-sm text-[#f5f5f0]/80 font-light">{b.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {["Medical Grade", "Plant-Based", "Cruelty-Free", "Sulfate-Free", "Paraben-Free", "Phthalate-Free"].map((label) => (
              <div key={label} data-testid={`badge-${label.toLowerCase().replace(/ /g, "-")}`} className="flex items-center gap-2 rounded-sm border border-[#c5a059]/25 bg-[#121212] px-3 py-2.5">
                <CheckCircle2 size={15} className="text-[#c5a059] shrink-0" />
                <span className="text-[11px] sm:text-xs uppercase tracking-[0.1em] text-[#f5f5f0]/85 font-medium">{label}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const MERCH = [
  { key: "tee", sku: "tee_crest", name: "Royal King Crest Tee", price: 40, sizes: ["L", "XL", "2XL", "3XL"], img: "/tee-crest.jpg", back: "/tee-back.jpg", blurb: "Wear the crown off the throne. Soft black cotton with a small gold \"Royal King Skin Co.\" over the left chest. On the back, the gold Royal King crest with \"Step in as a man\" above and \"Walk out as a king\" below." },
  { key: "hoodie", sku: "hoodie_crest", name: "Royal King Crest Hoodie", price: 60, sizes: ["XL", "2XL", "3XL"], img: "/hoodie-front.jpg", back: "/hoodie-back.jpg", blurb: "Heavyweight black fleece with gold-tipped drawstrings. A small gold Royal King crest with \"Royal King Skin Co\" arched above it on the chest, and the full gold crest with \"Step in as a man\" above and \"Walk out as a king\" below across the back." },
];

const MerchItem = ({ m, i }) => {
  const { storeOpen, add, setOpen, stock } = useStore();
  const [size, setSize] = useState(null);
  const [side, setSide] = useState("front");
  const sku = size && `${m.sku}_${size}`;
  const out = sku && stock[sku] === "out";
  const buy = () => {
    add({ sku, name: `${m.name} (${size})`, price: m.price, img: m.img });
    toast.success(`${m.name} (${size}) ${storeOpen ? "added to your order" : "pre-ordered to your cart"}`, { action: { label: "View Cart", onClick: () => setOpen(true) } });
  };
  return (
    <Reveal delay={i * 0.1}>
      <div data-testid={`merch-${m.key}`} className="group">
        <div className="relative aspect-square overflow-hidden rounded-sm border border-[#c5a059]/25 bg-[#0a0907]">
          <img loading="lazy" decoding="async" data-testid={`merch-${m.key}-image`} src={side === "back" && m.back ? m.back : m.img} alt={`${m.name} ${side}`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          {m.back && (
            <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-1 rounded-full border border-[#c5a059]/40 bg-black/60 p-1 backdrop-blur-md">
              {["front", "back"].map((v) => (
                <button key={v} data-testid={`merch-${m.key}-view-${v}`} onClick={() => setSide(v)} aria-pressed={side === v}
                  className={`rounded-full px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] transition-colors ${side === v ? "bg-[#c5a059] text-black" : "text-[#f5f5f0]/80 hover:text-[#d4af37]"}`}>{v}</button>
              ))}
            </div>
          )}
        </div>
        <div className="mt-6 flex items-baseline justify-between gap-4">
          <h3 data-testid={`merch-${m.key}-name`} className="font-serif text-2xl sm:text-3xl text-[#f5f5f0]">{m.name}</h3>
          <p data-testid={`merch-${m.key}-price`} className="font-serif text-xl text-[#d4af37]">${m.price}</p>
        </div>
        <p className="mt-2 text-sm text-[#f5f5f0]/70 font-light leading-relaxed">{m.blurb}</p>
        <div className="mt-5 flex flex-wrap gap-2">
          {m.sizes.map((z) => (
            <button key={z} data-testid={`merch-${m.key}-size-${z}`} onClick={() => setSize(z)} aria-pressed={size === z}
              className={`min-w-[56px] rounded-sm border px-4 py-2.5 text-sm transition-colors ${size === z ? "border-[#c5a059] bg-[#c5a059] text-black" : "border-white/20 text-[#f5f5f0] hover:border-[#c5a059]"}`}>{z}</button>
          ))}
        </div>
        <button data-testid={`merch-${m.key}-add-button`} disabled={!size || out} onClick={buy}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-[#c5a059] py-3.5 text-sm font-semibold text-black hover:bg-[#d4af37] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
          <Crown size={15} /> {out ? "Sold Out" : !size ? "Choose a Size" : storeOpen ? "Add to Cart" : "Pre-order Now"}
        </button>
        {!storeOpen && <p className="mt-2 text-center text-[11px] uppercase tracking-[0.2em] text-[#c5a059]/80">Ships in 7–10 days</p>}
      </div>
    </Reveal>
  );
};

const Merch = () => (
  <section id="merch" data-testid="merch-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-28">
    <Reveal>
      <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">The Royal Wardrobe</p>
      <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#f5f5f0]">Wear the crown.</h2>
    </Reveal>
    <div className="mt-12 grid grid-cols-1 gap-14 md:grid-cols-2 md:gap-10">
      {MERCH.map((m, i) => <MerchItem key={m.key} m={m} i={i} />)}
    </div>
    <GroomingKitTeaser />
  </section>
);

const GroomingKitTeaser = () => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/subscribe`, { email, source: "grooming-kit" });
      setDone(true);
      toast.success("You're on the list. We'll tell you the moment the kit arrives.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Please check your email and try again.");
    } finally { setBusy(false); }
  };
  return (
    <Reveal>
      <div data-testid="grooming-kit-teaser" className="mt-20 grid items-center gap-10 rounded-sm border border-[#c5a059]/25 bg-[#111] p-6 sm:p-10 lg:grid-cols-2 lg:gap-16">
        <img src="/grooming-kit.webp" alt="Royal King grooming kit with beard oils, balm, comb, brush and scissors" loading="lazy" className="w-full rounded-sm bg-white" />
        <div>
          <span data-testid="grooming-kit-badge" className="inline-flex items-center gap-2 border border-[#c5a059]/50 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] text-[#c5a059]"><Crown size={12} /> Coming Soon</span>
          <h3 className="mt-5 font-serif text-3xl sm:text-4xl tracking-tight text-[#f5f5f0]">The Royal Grooming Kit</h3>
          <p className="mt-4 max-w-md text-[#f5f5f0]/75 font-light leading-relaxed">Beard oils, a beard balm, a wooden comb, a boar-bristle brush and grooming scissors, all in one black-and-gold box. Be the first to know when it's ready.</p>
          {done ? (
            <p data-testid="grooming-kit-success" className="mt-8 font-serif text-xl text-[#d4af37]">You're on the list, King.</p>
          ) : (
            <form onSubmit={submit} className="mt-8 flex max-w-md flex-col gap-3 sm:flex-row">
              <input data-testid="grooming-kit-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email"
                className="flex-1 rounded-sm border border-white/15 bg-black/40 px-4 py-3.5 text-sm text-[#f5f5f0] placeholder:text-[#f5f5f0]/40 focus:border-[#c5a059] focus:outline-none" />
              <button data-testid="grooming-kit-notify" disabled={busy} className="rounded-sm bg-[#c5a059] px-7 py-3.5 text-sm font-medium text-black hover:bg-[#d4af37] transition-colors disabled:opacity-60">
                {busy ? "Saving…" : "Notify Me"}
              </button>
            </form>
          )}
        </div>
      </div>
    </Reveal>
  );
};

const INSIDE = [
  { name: "Creamy Cleansing Balm", step: "Rise", actives: "Shea Butter · Jojoba Oil · Squalane · Sweet Almond Oil · Vitamin E", for: "All skin types, especially dry skin" },
  { name: "Blue Clay Exfoliation Mask", step: "Refine", actives: "Blue Clay · Kaolin Clay · Glycolic Acid · Salicylic Acid · Niacinamide · Botanical Brighteners", for: "Dull, uneven skin" },
  { name: "Ingrown Hair Serum", step: "Refine", actives: "Exfoliating Complex · Salicylic Acid · Glycolic Acid · Tea Tree Oil · Allantoin · Aloe Vera", for: "Areas prone to ingrown hairs" },
  { name: "Oil-Free Moisturizer", step: "Restore", actives: "Hyaluronic Acid · Glycerin · Niacinamide · Pro-Vitamin B5", for: "All skin types, especially oily and combination" },
  { name: "Mineral SPF 50", step: "Reign", actives: "Zinc Oxide · Titanium Dioxide · Antioxidant Botanicals · Hyaluronic Acid · Vitamin E", for: "All skin types, every day" },
];

const InsideTheJar = () => (
  <section id="inside" data-testid="inside-the-jar" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-28">
    <Reveal>
      <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">Inside the Jar</p>
      <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#f5f5f0]">What every King puts on his skin</h2>
      <p className="mt-4 max-w-xl text-[#f5f5f0]/75 font-light leading-relaxed">The key ingredients in each product, straight from the formula. Nothing hidden.</p>
    </Reveal>
    <div className="mt-12 divide-y divide-white/10 border-y border-white/10">
      {INSIDE.map((x, i) => (
        <Reveal key={x.name} delay={i * 0.05}>
          <div data-testid={`inside-${i}`} className="grid grid-cols-1 gap-3 py-7 lg:grid-cols-[140px_minmax(0,1fr)_minmax(0,1.6fr)] lg:gap-10 lg:items-baseline">
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#c5a059]">0{i + 1} · {x.step}</span>
            <div>
              <h3 className="font-serif text-2xl text-[#f5f5f0]">{x.name}</h3>
              <p className="mt-1 text-xs text-[#f5f5f0]/55">Best for: {x.for}</p>
            </div>
            <p className="text-sm leading-relaxed text-[#f5f5f0]/80">{x.actives}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------- Mission Band ---------------- */
const MissionBand = () => (
  <section className="border-b border-white/10 bg-[#0d0d0d] py-20 lg:py-28">
    <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">Our Promise</p>
        <p className="mt-6 font-serif text-lg sm:text-xl lg:text-2xl leading-relaxed tracking-tight font-light text-[#c5a059]">
          Royal King Skin Co. is a premium men's skincare line built on quality, simplicity and results — carefully crafted formulations, proudly made in the USA.
        </p>
        <div className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#c5a059]">
          <Crown size={14} /> Made in the USA · Built for Men
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Editorial Banner ---------------- */
const EditorialBanner = ({ img, overline, quote }) => (
  <section className="relative h-[380px] lg:h-[460px] overflow-hidden border-y border-white/10">
    <img loading="lazy" decoding="async" src={img} alt="" className="absolute inset-0 w-full h-full object-cover animate-kenburns" />
    <div className="absolute inset-0 bg-black/55" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/40 to-transparent" />
    <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex items-center">
      <Reveal className="max-w-xl">
        <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">{overline}</p>
        <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-[#f5f5f0]">
          {quote}
        </h2>
      </Reveal>
    </div>
  </section>
);

/* ---------------- About ---------------- */
const About = () => (
  <section id="about" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <div className="relative h-[460px] overflow-hidden rounded-sm border border-white/10">
          <img loading="lazy" decoding="async" src={IMG.manSerum} alt="Man applying Royal King Skin Co. moisturizer" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/50 to-transparent" />
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">Our Kingdom · Our Story</p>
        <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#f5f5f0]">Born to Rule</h2>
        <p className="mt-6 text-[#f5f5f0]/85 font-light leading-relaxed text-lg">
          Royal King Skin Co. was built for men who want to look and feel their best — without a complicated routine. We hand-picked premium, effective skincare with carefully selected ingredients, proudly made in the USA.
        </p>
        <p className="mt-4 text-[#f5f5f0]/80 font-light leading-relaxed">
          Safe, effective and made for real life. From your first cleanse to your daily SPF, every product is designed to bring confidence and consistency to your everyday routine.
        </p>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Founder ---------------- */
const FounderVideo = ({ src = "/founder-welcome.mp4", title = "A Welcome From Dante", testid = "founder-video" }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const start = () => {
    window.__rkFeature = true;
    window.dispatchEvent(new CustomEvent("rk-feature", { detail: true }));
    window.dispatchEvent(new CustomEvent("rk-spa", { detail: true }));
    ref.current.currentTime = 0;
    ref.current.play().then(() => setPlaying(true)).catch(() => {});
  };
  const end = () => {
    setPlaying(false);
    window.__rkFeature = false;
    window.dispatchEvent(new CustomEvent("rk-feature", { detail: false }));
    window.dispatchEvent(new CustomEvent("rk-spa", { detail: false }));
  };
  return (
    <div className="relative overflow-hidden rounded-sm border border-[#c5a059]/30 shadow-[0_0_60px_rgba(197,160,89,0.15)]">
      <video ref={ref} data-feature="1" data-testid={testid} src={src} poster={IMG.founder} playsInline preload="none" controls={playing}
        onEnded={end} onPause={() => ref.current?.ended || end()} aria-label="A welcome from Dante Currington" className="block w-full h-full object-cover" />
      {!playing && (
        <button data-testid={`${testid}-play`} onClick={start} className="group absolute inset-0 flex flex-col items-center justify-end bg-gradient-to-t from-black/80 via-black/10 to-transparent pb-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[#c5a059] bg-black/50 backdrop-blur transition-transform duration-300 group-hover:scale-110">
            <Play size={22} className="ml-1 fill-[#c5a059] text-[#c5a059]" />
          </span>
          <span className="mt-4 text-[11px] uppercase tracking-[0.3em] text-[#f5f5f0]">{title}</span>
          <span className="mt-1 text-[10px] tracking-[0.15em] text-[#f5f5f0]/45">Digital greeting · sound on</span>
        </button>
      )}
    </div>
  );
};

const Founder = () => (
  <section id="founder" className="border-y border-white/10 bg-[#0d0d0d] py-24 lg:py-32">
    <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <div className="relative mx-auto w-full max-w-sm overflow-hidden rounded-sm border border-[#c5a059]/30 shadow-[0_0_60px_rgba(197,160,89,0.15)]">
          <div className="flex flex-col items-center bg-[#0a0907] px-8 pt-8 pb-6">
            <RoaringLogo testId="founder-crest" className="w-full h-auto drop-shadow-[0_0_35px_rgba(197,160,89,0.3)]" />
            <span className="mt-3 text-[11px] uppercase tracking-[0.3em] text-[#e8c877]">Tap the lion to roar</span>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">From the Founder</p>
        <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#f5f5f0]">
          The Lone Ranger Behind the Crown
        </h2>
        <div data-testid="founder-note" className="mt-8 space-y-4 border-l border-[#c5a059]/40 pl-6 text-[#f5f5f0]/80 font-light leading-relaxed">
          <p className="text-lg text-[#f5f5f0]/90">For over 30 years, I've worked in wellness, helping men feel their best. Again and again, I saw men dealing with razor bumps, dry skin, and products that were never made for them.</p>
          <p>So I became the lone ranger: one man building a business from scratch, with a vision to take it worldwide.</p>
          <p>Royal King Skin Co. gives every man a simple, premium routine that makes him look and feel like the king he is. I'm building it with faith, hard work, and my family's future in mind.</p>
          <p className="font-serif italic text-2xl text-[#d4af37]">Step in as a man. Walk out as a king.</p>
        </div>
        <div data-testid="founder-signature" className="mt-8">
          <p className="font-serif italic text-3xl text-[#f5f5f0]">Dante Currington</p>
          <p className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#c5a059]"><Crown size={14} /> Founder, Royal King Skin Co.</p>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Reviews ---------------- */
const Reviews = () => (
  <section className="border-y border-white/10 bg-[#0d0d0d] py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal className="text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">Early Reviews</p>
        <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#f5f5f0]">Fit For Kings</h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        {REVIEWS.map((r, i) => (
          <Reveal key={r.name} delay={i * 0.1}>
            <div className="h-full p-8 rounded-sm border border-white/10 bg-[#121212]">
              <div className="flex gap-1 text-[#c5a059]">{[...Array(5)].map((_, k) => <Star key={k} size={15} fill="#c5a059" />)}</div>
              <p className="mt-5 font-serif italic text-lg text-[#f5f5f0]/85 leading-relaxed">“{r.quote}”</p>
              <div className="mt-6 text-sm font-medium text-[#f5f5f0]">{r.name}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Waitlist ---------------- */
const ShareSpot = ({ spot }) => {
  const url = `${window.location.origin}/#waitlist`;
  const text = spot && spot <= 100
    ? `I'm King #${spot} of the first 100 at Royal King Skin Co. Claim your spot before they're gone:`
    : "I just joined the Royal List at Royal King Skin Co. Join me:";
  const share = async () => {
    try {
      if (navigator.share) return await navigator.share({ title: "Royal King Skin Co.", text, url });
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
    await navigator.clipboard?.writeText(`${text} ${url}`).catch(() => {});
    toast.success("Invite copied, paste it anywhere");
  };
  return (
    <div data-testid="share-spot" className="mt-6 border-t border-[#c5a059]/20 pt-6">
      <p className="text-[11px] uppercase tracking-[0.25em] text-[#f5f5f0]/55">Bring your crew</p>
      <div className="mt-3 flex flex-wrap justify-center gap-2">
        <button data-testid="share-spot-button" onClick={share} className="inline-flex items-center gap-2 rounded-full border border-[#c5a059]/50 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-[#c5a059] hover:bg-[#c5a059] hover:text-black transition-colors duration-300">
          <Share2 size={13} /> Share My Spot
        </button>
        <a data-testid="share-spot-text" href={`sms:?&body=${encodeURIComponent(`${text} ${url}`)}`} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-[#f5f5f0]/75 hover:border-[#c5a059]/60 hover:text-[#c5a059] transition-colors duration-300">
          <MessageCircle size={13} /> Text a Friend
        </a>
      </div>
    </div>
  );
};

const RoyalWelcome = ({ spot, onClose }) => (
  <div data-testid="royal-welcome" className="fixed inset-0 z-[75] flex items-center justify-center bg-black/75 backdrop-blur-sm p-5" onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} className="founder-pop relative w-full max-w-md overflow-hidden rounded-sm border border-[#c5a059]/60 bg-[#0d0b08] text-center shadow-[0_40px_100px_-30px_rgba(197,160,89,0.45)]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(197,160,89,0.22),transparent_65%)] pointer-events-none" />
      <button data-testid="royal-welcome-close" onClick={onClose} aria-label="Close" className="absolute right-3 top-3 z-10 rounded-full p-1.5 text-[#f5f5f0]/60 hover:text-[#c5a059] transition-colors"><X size={16} /></button>
      <div className="relative px-8 pb-9 pt-10">
        <img loading="lazy" decoding="async" src="/logo-crest-800.webp" alt="Royal King Skin Co. crest" className="mx-auto h-24 w-24 rounded-full object-contain bg-black p-1 object-top ring-2 ring-[#c5a059] ring-offset-4 ring-offset-[#0d0b08]" />
        <p className="mt-6 text-[11px] uppercase tracking-[0.35em] text-[#c5a059]">Welcome to the Royal List</p>
        <h3 className="mt-3 font-serif text-3xl sm:text-4xl text-[#f5f5f0]">You're in, King.</h3>
        {spot && spot <= 100 && <p data-testid="royal-welcome-spot" className="mt-3 font-serif italic text-lg text-[#d4af37]">King #{spot} of the first 100</p>}
        <p className="mt-4 text-sm leading-relaxed text-[#f5f5f0]/70">Thank you for standing with us from day one. You'll be the first to know when we open, and your welcome email is on its way.</p>
        <p className="mt-6 font-serif italic text-xl text-[#f5f5f0]">Dante Currington</p>
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#c5a059]/80">Founder, Royal King Skin Co.</p>
        <ShareSpot spot={spot} />
        <button data-testid="royal-welcome-continue" onClick={onClose} className="mt-7 inline-flex items-center gap-2 rounded-sm bg-[#c5a059] px-7 py-3 text-sm font-semibold text-black hover:bg-[#d4af37] transition-colors"><Crown size={15} /> Continue</button>
      </div>
    </div>
  </div>
);

const Waitlist = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [bMonth, setBMonth] = useState("");
  const [bDay, setBDay] = useState("");
  const [welcome, setWelcome] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/subscribe`, { email, source: "launch-waitlist", birthday: bMonth && bDay ? `${bMonth}-${bDay}` : null });
      setWelcome({ spot: r.data.spot });
      setEmail("");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : err.response?.status === 422 ? "Please enter a valid email and birthday." : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="waitlist" className="relative overflow-hidden py-24 lg:py-32">
      {welcome && <RoyalWelcome spot={welcome.spot} onClose={() => setWelcome(null)} />}
      <div className="absolute inset-0">
        <img loading="lazy" decoding="async" src={IMG.lineup} alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-[#0a0a0a]/85" />
      </div>
      <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
        <Reveal>
          <Crown size={32} className="mx-auto text-[#c5a059]" />
          <p className="mt-6 text-xs uppercase tracking-[0.35em] text-[#c5a059]">Royal King Skin Co. Is Coming</p>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#f5f5f0]">Join the Royal List</h2>
          <p className="mt-4 text-[#f5f5f0]/80 font-light">
            Be among the first to experience the collection. Join the Royal List for early access, exclusive founding-king pricing, and a royal welcome gift.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <form data-testid="waitlist-form" onSubmit={submit} className="mt-10 max-w-md mx-auto">
           <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
            <input
              data-testid="waitlist-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full rounded-sm bg-black/60 border border-white/10 px-4 py-3.5 text-sm text-[#f5f5f0] placeholder:text-[#f5f5f0]/30 focus:border-[#c5a059] focus:outline-none focus:ring-1 focus:ring-[#c5a059] transition-colors"
            />
            <EmailHint value={email} onPick={setEmail} testid="waitlist-email" className="mt-2 text-left" />
            </div>
            <button data-testid="waitlist-submit" type="submit" disabled={loading} className="rounded-sm bg-[#c5a059] px-7 py-3.5 text-sm font-medium text-black hover:bg-[#d4af37] transition-colors disabled:opacity-60">
              {loading ? "Joining…" : "Join the Royal List"}
            </button>
           </div>
           <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-xs text-[#f5f5f0]/60">
             <span>Birthday <span className="text-[#f5f5f0]/40">(optional — 15% off every year)</span></span>
             <select data-testid="waitlist-birth-month" value={bMonth} onChange={(e) => setBMonth(e.target.value)} className="rounded-sm bg-black/60 border border-white/10 px-2 py-1.5 text-[#f5f5f0] focus:border-[#c5a059] outline-none">
               <option value="">Month</option>
               {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>)}
             </select>
             <select data-testid="waitlist-birth-day" value={bDay} onChange={(e) => setBDay(e.target.value)} className="rounded-sm bg-black/60 border border-white/10 px-2 py-1.5 text-[#f5f5f0] focus:border-[#c5a059] outline-none">
               <option value="">Day</option>
               {Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0")).map((d) => <option key={d} value={d}>{Number(d)}</option>)}
             </select>
           </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- Retail & Wholesale Partners ---------------- */
const RETAIL_SIGNALS = [
  { icon: Flag, title: "Proudly Made in the USA", desc: "Every formula is manufactured and filled in the United States — quality you can stand behind on your shelves." },
  { icon: Package, title: "Retail-Ready", desc: "UPCs assigned and case packs available — ready for shelf, endcap and online retail from day one." },
  { icon: CheckCircle2, title: "Medical-Grade Formulas", desc: "Medical-grade, skin-friendly ingredients — professional quality with no harsh fillers. The real deal men can trust." },
];

const RETAIL_STATS = ["5 Hero SKUs", "UPCs Assigned", "Case Packs Ready", "Made in USA"];

const RetailPartners = () => {
  const [form, setForm] = useState({ name: "", company: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes("@")) {
      toast.error("Please add your name and a valid email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/wholesale`, form);
      toast.success("Received — we'll follow up with wholesale details shortly.");
      setSent(true);
      setForm({ name: "", company: "", email: "", message: "" });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="wholesale" className="relative overflow-hidden border-y border-[#c5a059]/25 bg-[#0d0d0d] py-24 lg:py-32">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, #c5a059 0%, transparent 45%)" }} />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        {/* Left — the pitch */}
        <div>
          <Reveal>
            <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#c5a059]">
              <Building2 size={14} /> Royal Partners · Retail &amp; Wholesale
            </p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#f5f5f0]">
              Ready for Your <span className="italic text-[#d4af37]">Shelves</span>.
            </h2>
            <p className="mt-5 max-w-lg text-[#f5f5f0]/85 font-light leading-relaxed text-lg">
              Royal King Skin Co. is a premium, American-made men's skincare line built to move — a tight, confident lineup your customers actually understand. Let's talk partnership.
            </p>
          </Reveal>

          <div className="mt-10 space-y-4">
            {RETAIL_SIGNALS.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={i * 0.08}>
                  <div data-testid={`retail-signal-${i}`} className="flex items-start gap-4 rounded-sm border border-white/10 bg-[#121212] p-5 hover:border-[#c5a059]/40 transition-colors">
                    <span className="shrink-0 inline-flex h-11 w-11 items-center justify-center rounded-sm border border-[#c5a059]/40 bg-[#c5a059]/10 text-[#c5a059]">
                      <Icon size={20} />
                    </span>
                    <div>
                      <h3 className="font-serif text-lg text-[#f5f5f0]">{s.title}</h3>
                      <p className="mt-1 text-[15px] text-[#f5f5f0]/80 font-light leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2">
              {RETAIL_STATS.map((stat, i) => (
                <span key={stat} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#f5f5f0]/60">
                  {i > 0 && <span className="h-3 w-px bg-white/15" />}
                  <CheckCircle2 size={12} className="text-[#c5a059]" /> {stat}
                </span>
              ))}
            </div>
            <p className="mt-6 flex items-center gap-2 text-sm text-[#f5f5f0]/80 font-light">
              <Mail size={16} className="text-[#c5a059]" />
              Prefer email? Reach us directly at{" "}
              <a href="mailto:Dante@royalkingskinco.com" data-testid="retail-email-link" className="text-[#c5a059] hover:text-[#d4af37] transition-colors">Dante@royalkingskinco.com</a>
            </p>
          </Reveal>
        </div>

        {/* Right — the form */}
        <Reveal delay={0.1}>
          <div className="rounded-sm border border-[#c5a059]/30 bg-[#0a0a0a] p-7 lg:p-9 shadow-[0_0_60px_rgba(197,160,89,0.12)]">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#c5a059]">
              <Crown size={14} /> Request Wholesale Info
            </div>
            <p className="mt-3 text-[15px] text-[#f5f5f0]/80 font-light leading-relaxed">
              Buyers and retailers — tell us a little about your store and we'll send pricing, case packs and everything you need.
            </p>
            <form data-testid="wholesale-form" onSubmit={submit} className="mt-7 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-[#f5f5f0]/60 mb-2">Your Name *</label>
                  <input
                    data-testid="wholesale-name"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Buyer"
                    className="w-full rounded-sm bg-black/60 border border-white/10 px-4 py-3 text-sm text-[#f5f5f0] placeholder:text-[#f5f5f0]/30 focus:border-[#c5a059] focus:outline-none focus:ring-1 focus:ring-[#c5a059] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-[#f5f5f0]/60 mb-2">Company / Store</label>
                  <input
                    data-testid="wholesale-company"
                    type="text"
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Retailer name"
                    className="w-full rounded-sm bg-black/60 border border-white/10 px-4 py-3 text-sm text-[#f5f5f0] placeholder:text-[#f5f5f0]/30 focus:border-[#c5a059] focus:outline-none focus:ring-1 focus:ring-[#c5a059] transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#f5f5f0]/60 mb-2">Work Email *</label>
                <input
                  data-testid="wholesale-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@company.com"
                  className="w-full rounded-sm bg-black/60 border border-white/10 px-4 py-3 text-sm text-[#f5f5f0] placeholder:text-[#f5f5f0]/30 focus:border-[#c5a059] focus:outline-none focus:ring-1 focus:ring-[#c5a059] transition-colors"
                />
                <EmailHint value={form.email} onPick={(v) => setForm((f) => ({ ...f, email: v }))} testid="wholesale-email" className="mt-2" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#f5f5f0]/60 mb-2">What are you looking for?</label>
                <textarea
                  data-testid="wholesale-message"
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your store, locations, and what you'd like to carry…"
                  className="w-full rounded-sm bg-black/60 border border-white/10 px-4 py-3 text-sm text-[#f5f5f0] placeholder:text-[#f5f5f0]/30 focus:border-[#c5a059] focus:outline-none focus:ring-1 focus:ring-[#c5a059] transition-colors resize-none"
                />
              </div>
              <button
                data-testid="wholesale-submit"
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-[#c5a059] px-7 py-4 text-sm font-medium tracking-wide text-black hover:bg-[#d4af37] transition-colors disabled:opacity-60"
              >
                {loading ? "Sending…" : sent ? "Request Sent ✓" : (<><Send size={16} /> Request Wholesale Info</>)}
              </button>
              <p className="text-center text-xs text-[#f5f5f0]/45 font-light">We'll never share your details. Expect a reply within a few business days.</p>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- Footer ---------------- */
const Footer = () => (
  <footer id="contact" className="border-t border-white/10 bg-[#0d0d0d]">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-3 gap-12">
      <div>
        <div className="flex items-center gap-3">
          <img loading="lazy" decoding="async" data-testid="footer-logo" src={LOGO} alt="Royal King Skin Co." className="h-16 w-auto" />
          <span className="font-serif text-lg text-[#f5f5f0]">Royal King Skin Co.</span>
        </div>
        <p className="mt-4 text-[15px] text-[#f5f5f0]/72 font-light leading-relaxed">
          Luxury grooming & skincare for the modern king. Est. 2026.
        </p>
        <div className="mt-5 flex gap-3">
          <a href={IG_URL} target="_blank" rel="noopener noreferrer" aria-label="Royal King on Instagram" data-testid="social-instagram" className="p-2 rounded-sm border border-white/10 text-[#f5f5f0]/85 hover:text-[#c5a059] hover:border-[#c5a059]/40 transition-colors"><Instagram size={18} /></a>
        </div>
        <button
          data-testid="footer-install-app"
          onClick={() => window.dispatchEvent(new Event("rk-open-install"))}
          className="mt-5 inline-flex items-center gap-2 rounded-sm border border-[#c5a059]/50 px-4 py-2.5 text-sm font-medium text-[#c5a059] hover:bg-[#c5a059] hover:text-black transition-colors"
        >
          <Smartphone size={16} /> Add App to Your Phone
        </button>
      </div>
      <div>
        <h4 className="text-xs uppercase tracking-[0.2em] text-[#c5a059]">Contact</h4>
        <p className="mt-4 flex items-center gap-2 text-sm text-[#f5f5f0]/80 font-light"><Mail size={16} className="text-[#c5a059]" /> Dante@royalkingskinco.com</p>
        <p className="mt-3 flex items-start gap-2 text-sm text-[#f5f5f0]/80 font-light"><MapPin size={16} className="mt-0.5 text-[#c5a059]" /> Shipping across the USA · Ships in 7–10 days · Free over $100</p>
      </div>
      <div>
        <h4 className="text-xs uppercase tracking-[0.2em] text-[#c5a059]">Explore</h4>
        <div className="mt-4 flex flex-col gap-2 text-sm">
          {[["Products", "products"], ["Ingredients", "inside"], ["Guides", "guides"], ["Shipping", "shipping"], ["Wholesale", "wholesale"], ["About", "about"], ["Join the Royal List", "waitlist"]].map(([l, id]) => (
            <button key={id} onClick={() => scrollTo(id)} className="text-left text-[#f5f5f0]/80 hover:text-[#c5a059] transition-colors font-light">{l}</button>
          ))}
        </div>
      </div>
    </div>
    <div className="border-t border-white/10 py-6 text-center text-xs text-[#f5f5f0]/62">
      © {new Date().getFullYear()} Royal King Skin Co. All rights reserved.
      <div className="mt-1 text-[#f5f5f0]/35 text-[10px]">Music: "Night on the Docks - Sax" and "Bossa Antigua" by Kevin MacLeod (incompetech.com) · CC BY 4.0</div>
    </div>
  </footer>
);

/* ---------------- SEO Content ---------------- */
const SEO_POINTS = [
  "Premium skincare made for men",
  "High-quality, carefully selected ingredients",
  "Simple daily grooming routines",
  "Keep skin comfortable, healthy and ready for the day",
  "Luxury grooming with professional results",
];

const SeoContent = () => (
  <section className="border-t border-white/10 bg-[#0d0d0d] py-24 lg:py-32">
    <div className="max-w-4xl mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">Premium Men's Grooming &amp; Skincare</p>
        <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#f5f5f0]">
          Look Your Best. Feel Like a <span className="italic text-[#d4af37]">King</span>.
        </h2>
        <p className="mt-6 text-[#f5f5f0]/85 font-light leading-relaxed text-lg">
          Welcome to Royal King Skin Co., where premium men's grooming meets high-performance skincare. We bring together products chosen specifically for men who want healthy, confident, younger-looking skin — without complicated routines.
        </p>
        <p className="mt-4 text-[#f5f5f0]/80 font-light leading-relaxed">
          Whether you're clean-shaven or bearded, Royal King Skin Co. keeps men's grooming simple — a premium cleanser, moisturizer, mineral sunscreen and exfoliating mask, all made in the USA and built to deliver quality, luxury and results.
        </p>
      </Reveal>

      <Reveal delay={0.1}>
        <h3 className="mt-12 font-serif text-2xl text-[#f5f5f0]">Why Choose Royal King Skin Co.?</h3>
        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SEO_POINTS.map((pt) => (
            <li key={pt} data-testid={`seo-point-${pt.split(" ")[0].toLowerCase()}`} className="flex items-start gap-3 text-[#f5f5f0]/75 font-light">
              <Crown size={16} className="mt-1 shrink-0 text-[#c5a059]" /> {pt}
            </li>
          ))}
        </ul>
      </Reveal>

      <Reveal delay={0.15}>
        <p className="mt-12 text-[#f5f5f0]/80 font-light leading-relaxed">
          At Royal King Skin Co., we believe every man deserves to look confident and feel his best. Our mission is simple: premium, honest skincare made in the USA that fits a man's real life — no 12-step routine, just a few powerful products that work.
        </p>
        <p className="mt-4 text-[#f5f5f0]/80 font-light leading-relaxed">
          Whether you're clean-shaven or bearded, we've made men's skincare simple. If you're searching for men's grooming products, men's skincare, luxury skincare for men, a men's cleanser, men's face moisturizer, mineral sunscreen for men, a men's exfoliating mask, beard care, or a simple men's skincare routine — you've come to the right place.
        </p>
        <p className="mt-8 font-serif italic text-xl text-[#c5a059]">
          Royal King Skin Co. — Where Kings Take Care of Their Skin.
        </p>
      </Reveal>
    </div>
  </section>
);


/* ---------------- App ---------------- */
/* ---------------- Routine CTA + Skin Advisor ---------------- */
const findProduct = (name) => PRODUCTS.find((p) => p.name === name);

const openAdvisor = () => window.dispatchEvent(new CustomEvent("open-advisor"));

const QUIZ = [
  { key: "concern", q: "What's your biggest skin concern?", options: ["Fine lines & aging", "Dark spots & dullness", "Dryness & dehydration", "Oiliness & shine", "Rough texture", "Ingrown hairs", "Tired, puffy eyes"] },
  { key: "type", q: "How would you describe your skin?", options: ["Oily", "Dry", "Combination", "Not sure"] },
  { key: "time", q: "How much time do you want to spend?", options: ["Give me 2 minutes", "A standard routine", "The full ritual"] },
  { key: "shave", q: "Do you shave regularly?", options: ["Yes", "No"] },
  { key: "sun", q: "How much sun do you get?", options: ["Not much", "A moderate amount", "A lot"] },
  { key: "depth", q: "Basic or complete routine?", options: ["Keep it basic", "Give me the complete routine"] },
];

const STEP_ORDER = ["Rise", "Refine", "Restore", "Reign"];

const buildRoutine = (a) => {
  const steps = [
    { step: "Rise", name: "Creamy Cleansing Balm (4 oz / 120 mL)" },
    { step: "Refine", name: "Blue Clay Exfoliation Mask (1.7 oz)" },
    { step: "Restore", name: "Oil-Free Moisturizer (1.7 oz)" },
    { step: "Reign", name: "Mineral SPF 50 (1.7 oz)" },
  ];
  let result = steps;
  if (a.time === "Give me 2 minutes" || a.depth === "Keep it basic") {
    result = steps.filter((s) => s.step !== "Refine");
  }
  if (a.concern === "Ingrown hairs" || a.shave === "Yes") result = [...result, { step: "Refine", name: "Ingrown Hair Serum (1.7 oz)" }];
  result.sort((x, y) => STEP_ORDER.indexOf(x.step) - STEP_ORDER.indexOf(y.step));
  return result;
};

const SkinAdvisor = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  const reset = () => { setStep(0); setAnswers({}); setDone(false); };
  const close = () => { reset(); onClose(); };

  const pick = (val) => {
    const key = QUIZ[step].key;
    const next = { ...answers, [key]: val };
    setAnswers(next);
    if (step < QUIZ.length - 1) setStep(step + 1);
    else setDone(true);
  };

  if (!open) return null;
  const routine = done ? buildRoutine(answers) : [];
  const current = QUIZ[step];

  return (
    <div data-testid="advisor-modal" className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={close} />
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-sm border border-[#c5a059]/30 bg-[#121212] p-8 lg:p-10 shadow-[0_0_60px_rgba(197,160,89,0.15)]"
      >
        <button data-testid="advisor-close" onClick={close} className="absolute top-5 right-5 text-[#f5f5f0]/80 hover:text-[#c5a059] transition-colors">
          <X size={22} />
        </button>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#c5a059]">
          <Crown size={14} /> Royal King Skin Advisor
        </div>

        {!done ? (
          <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}>
            <div className="mt-4 flex items-center gap-3">
              <span className="text-xs text-[#f5f5f0]/62">Step {step + 1} of {QUIZ.length}</span>
              <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-[#c5a059] transition-all duration-300" style={{ width: `${((step + 1) / QUIZ.length) * 100}%` }} />
              </div>
            </div>
            <h3 className="mt-4 font-serif text-2xl sm:text-3xl text-[#f5f5f0]">{current.q}</h3>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {current.options.map((opt) => (
                <button
                  key={opt}
                  data-testid={`advisor-option-${opt.split(" ")[0].toLowerCase()}`}
                  onClick={() => pick(opt)}
                  className="text-left rounded-sm border border-white/10 px-4 py-3 text-sm text-[#f5f5f0]/80 hover:border-[#c5a059] hover:bg-[#c5a059]/10 transition-all"
                >
                  {opt}
                </button>
              ))}
            </div>
            {step > 0 && (
              <button data-testid="advisor-back" onClick={() => setStep(step - 1)} className="mt-6 text-xs uppercase tracking-[0.2em] text-[#f5f5f0]/72 hover:text-[#c5a059] transition-colors">
                ← Back
              </button>
            )}
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="mt-5">
            <h3 className="font-serif text-2xl sm:text-3xl text-[#f5f5f0]">Your Royal Routine</h3>
            <p className="mt-2 text-sm text-[#f5f5f0]/80 font-light">Built around your answers — simple steps, real results.</p>
            <div className="mt-6 space-y-3">
              {routine.map(({ step: st, name }) => {
                const p = findProduct(name);
                if (!p) return null;
                return (
                  <div key={name} data-testid={`advisor-pick-${name.split(" ")[0].toLowerCase()}`} className="flex items-center gap-4 rounded-sm border border-white/10 bg-[#0d0d0d] p-3">
                    <img loading="lazy" decoding="async" src={p.img} alt={p.name} className="h-14 w-14 rounded-sm object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[9px] uppercase tracking-[0.2em] text-[#c5a059]">{st}</div>
                      <div className="font-serif text-[#f5f5f0] truncate">{p.name}</div>
                    </div>
                    <span className="font-serif text-[#c5a059] shrink-0">
                      {p.price ? `$${Number.isInteger(p.price) ? p.price : p.price.toFixed(2)}` : ""}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="advisor-join"
                onClick={() => { close(); scrollTo("products"); }}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-sm bg-[#c5a059] px-8 py-4 text-sm font-medium tracking-wide text-black hover:bg-[#d4af37] transition-colors"
              >
                <Crown size={18} /> Shop These Products
              </button>
              <button
                data-testid="advisor-restart"
                onClick={reset}
                className="rounded-sm border border-white/15 px-6 py-4 text-sm font-medium text-[#f5f5f0]/85 hover:border-[#c5a059] hover:text-[#c5a059] transition-colors"
              >
                Start Over
              </button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

const RoutineCTA = () => {
  const [advisorOpen, setAdvisorOpen] = useState(false);
  useEffect(() => {
    const handler = () => setAdvisorOpen(true);
    window.addEventListener("open-advisor", handler);
    return () => window.removeEventListener("open-advisor", handler);
  }, []);
  return (
    <section className="relative overflow-hidden border-y border-white/10 bg-[#0d0d0d] py-24 lg:py-32">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">Premium Men's Skincare Made Simple</p>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-[#f5f5f0]">
            Your skin. Your routine.<br /><span className="italic text-[#d4af37]">Your confidence.</span>
          </h2>
          <p className="mt-6 text-[#f5f5f0]/85 font-light leading-relaxed text-lg">
            At Royal King Skin Co., we believe every man deserves skincare that helps him look and feel his best. Our carefully selected formulations are made in the USA and built to bring confidence, consistency and quality to your everyday routine — whether you're clean-shaven or bearded.
          </p>
          <button
            data-testid="shop-royal-king-button"
            onClick={() => scrollTo("products")}
            className="mt-9 inline-flex items-center justify-center gap-2 rounded-sm bg-[#c5a059] px-10 py-4 text-sm font-medium tracking-wide text-black hover:bg-[#d4af37] transition-colors"
          >
            <Crown size={18} /> Shop Royal King Skin Co.
          </button>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-16 pt-12 border-t border-white/10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">Not Sure Where to Start?</p>
            <p className="mt-4 text-[#f5f5f0]/85 font-light leading-relaxed">
              Answer a few simple questions and let the Royal King Skin Advisor build your starting routine.
            </p>
            <button
              data-testid="find-routine-button"
              onClick={() => setAdvisorOpen(true)}
              className="mt-7 inline-flex items-center justify-center gap-2 rounded-sm border border-[#c5a059] px-10 py-4 text-sm font-medium tracking-wide text-[#c5a059] hover:bg-[#c5a059]/10 transition-colors"
            >
              <Sparkles size={17} /> Find My Royal Routine
            </button>
          </div>
        </Reveal>
      </div>
      <SkinAdvisor open={advisorOpen} onClose={() => setAdvisorOpen(false)} />
    </section>
  );
};



/* ---------------- Ask the King ---------------- */
const KING_SUGGESTIONS = [
  "I don't know anything about skincare.",
  "What should I use after shaving?",
  "I have oily skin — where do I start?",
  "What's the difference between a cleanser and a scrub?",
];

const AskTheKing = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "king", text: "I'm the King. Ask me anything about your skin — I'll keep it simple." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ask-king`, { message: q, session_id: sessionRef.current });
      sessionRef.current = res.data.session_id;
      setMessages((m) => [...m, { role: "king", text: res.data.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "king", text: "The King is resting for a moment — please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="ask-king-fab"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[55] inline-flex items-center gap-2 rounded-full bg-[#c5a059] pl-4 pr-5 py-3 text-sm font-medium text-black shadow-[0_8px_30px_rgba(197,160,89,0.35)] hover:bg-[#d4af37] transition-colors"
      >
        {open ? <X size={18} /> : <MessageCircle size={18} />}
        Ask the King
      </button>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          data-testid="ask-king-panel"
          className="fixed bottom-24 right-6 z-[55] flex flex-col w-[min(92vw,400px)] h-[min(70vh,560px)] rounded-sm border border-[#c5a059]/30 bg-[#121212] shadow-[0_0_60px_rgba(0,0,0,0.6)] overflow-hidden"
        >
          <div className="flex items-center gap-2 border-b border-white/10 bg-[#0d0d0d] px-5 py-4">
            <Crown size={16} className="text-[#c5a059]" />
            <span className="font-serif text-[#f5f5f0]">Ask the King</span>
            <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-[#c5a059]/60">AI Skin Guide</span>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`king-msg-${m.role}`}
                  className={`max-w-[85%] rounded-sm px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#c5a059] text-black" : "bg-[#0d0d0d] border border-white/10 text-[#f5f5f0]/85"}`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="rounded-sm border border-white/10 bg-[#0d0d0d] px-3.5 py-2.5 text-sm text-[#c5a059]">The King is thinking…</div>
              </div>
            )}
            {messages.length <= 1 && !loading && (
              <div className="mt-2 flex flex-col gap-2">
                {KING_SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    data-testid="king-suggestion"
                    onClick={() => send(s)}
                    className="text-left rounded-sm border border-white/10 px-3 py-2 text-xs text-[#f5f5f0]/85 hover:border-[#c5a059] hover:text-[#c5a059] transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2 border-t border-white/10 bg-[#0d0d0d] px-3 py-3"
          >
            <input
              data-testid="king-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about your skin…"
              className="flex-1 rounded-sm bg-black/60 border border-white/10 px-3 py-2.5 text-sm text-[#f5f5f0] placeholder:text-[#f5f5f0]/30 focus:border-[#c5a059] focus:outline-none transition-colors"
            />
            <button data-testid="king-send" type="submit" disabled={loading} className="rounded-sm bg-[#c5a059] p-2.5 text-black hover:bg-[#d4af37] transition-colors disabled:opacity-60">
              <Send size={16} />
            </button>
          </form>
        </motion.div>
      )}
    </>
  );
};


/* ---------------- What's Your King Type? ---------------- */
const KING_TYPES = {
  "Executive King": {
    tagline: "Polished, busy, and always on.",
    desc: "You move fast and want to look sharp without the fuss. Your routine is quick, clean and effective — in and out, always presentable.",
    picks: ["Creamy Cleansing Balm (4 oz / 120 mL)", "Oil-Free Moisturizer (1.7 oz)", "Mineral SPF 50 (1.7 oz)"],
  },
  "Rugged King": {
    tagline: "Outdoors, beard, built for the elements.",
    desc: "Sun, sweat and a beard are part of your life. You need protection, repair and something that keeps your skin (and shave) smooth and strong.",
    picks: ["Creamy Cleansing Balm (4 oz / 120 mL)", "Oil-Free Moisturizer (1.7 oz)", "Mineral SPF 50 (1.7 oz)", "Blue Clay Exfoliation Mask (1.7 oz)"],
  },
  "Classic King": {
    tagline: "Simple, timeless, no nonsense.",
    desc: "You like things straightforward and dependable. A clean, classic routine that just works — nothing you have to think about.",
    picks: ["Creamy Cleansing Balm (4 oz / 120 mL)", "Oil-Free Moisturizer (1.7 oz)", "Mineral SPF 50 (1.7 oz)", "Blue Clay Exfoliation Mask (1.7 oz)"],
  },
  "Young King": {
    tagline: "Starting early, building the habit.",
    desc: "You're getting ahead of the game. Start simple, build good habits now, and your skin will thank you for decades.",
    picks: ["Creamy Cleansing Balm (4 oz / 120 mL)", "Oil-Free Moisturizer (1.7 oz)", "Mineral SPF 50 (1.7 oz)"],
  },
  "Comeback King": {
    tagline: "Ready to take better care of yourself.",
    desc: "You're stepping back into it and ready for a real upgrade. Renew, brighten and restore — this is your comeback, and it starts now.",
    picks: ["Creamy Cleansing Balm (4 oz / 120 mL)", "Blue Clay Exfoliation Mask (1.7 oz)", "Oil-Free Moisturizer (1.7 oz)", "Mineral SPF 50 (1.7 oz)"],
  },
};
const KING_TYPE_ORDER = ["Executive King", "Rugged King", "Classic King", "Young King", "Comeback King"];

const KING_QUIZ = [
  { q: "How does your day usually look?", options: ["Back-to-back and busy", "Outdoors and on the move", "Steady and classic", "Just getting started in life", "Ready for a fresh start"] },
  { q: "Your relationship with skincare?", options: ["No time — keep it minimal", "Sun, sweat and a beard", "Simple and traditional", "Brand new to this", "Getting back into it"] },
  { q: "What matters most to you?", options: ["Look sharp, fast", "Protection and repair", "Timeless and easy", "Building good habits early", "Turning things around"] },
  { q: "Your look right now?", options: ["Clean and sharp", "Full beard, weathered", "Neat and classic", "Still figuring it out", "Rebuilding my confidence"] },
];

const KingTypeModal = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState([0, 0, 0, 0, 0]);
  const [done, setDone] = useState(false);

  const reset = () => { setStep(0); setScores([0, 0, 0, 0, 0]); setDone(false); };
  const close = () => { reset(); onClose(); };

  const pick = (idx) => {
    const next = scores.map((s, i) => (i === idx ? s + 1 : s));
    setScores(next);
    if (step < KING_QUIZ.length - 1) setStep(step + 1);
    else setDone(true);
  };

  if (!open) return null;
  const winnerIdx = scores.indexOf(Math.max(...scores));
  const typeName = KING_TYPE_ORDER[winnerIdx];
  const type = KING_TYPES[typeName];
  const current = KING_QUIZ[step];

  return (
    <div data-testid="kingtype-modal" className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={close} />
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-sm border border-[#c5a059]/30 bg-[#121212] p-8 lg:p-10 shadow-[0_0_60px_rgba(197,160,89,0.15)]"
      >
        <button data-testid="kingtype-close" onClick={close} className="absolute top-5 right-5 text-[#f5f5f0]/80 hover:text-[#c5a059] transition-colors">
          <X size={22} />
        </button>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#c5a059]">
          <Crown size={14} /> What's Your King Type?
        </div>

        {!done ? (
          <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}>
            <div className="mt-4 flex items-center gap-3">
              <span className="text-xs text-[#f5f5f0]/62">Question {step + 1} of {KING_QUIZ.length}</span>
              <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-[#c5a059] transition-all duration-300" style={{ width: `${((step + 1) / KING_QUIZ.length) * 100}%` }} />
              </div>
            </div>
            <h3 className="mt-4 font-serif text-2xl sm:text-3xl text-[#f5f5f0]">{current.q}</h3>
            <div className="mt-6 grid grid-cols-1 gap-3">
              {current.options.map((opt, idx) => (
                <button
                  key={opt}
                  data-testid={`kingtype-option-${idx}`}
                  onClick={() => pick(idx)}
                  className="text-left rounded-sm border border-white/10 px-4 py-3 text-sm text-[#f5f5f0]/80 hover:border-[#c5a059] hover:bg-[#c5a059]/10 transition-all"
                >
                  {opt}
                </button>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="mt-5 text-center">
            <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">You are the</p>
            <h3 data-testid="kingtype-result" className="mt-2 font-serif text-3xl sm:text-4xl text-[#d4af37]">{typeName}</h3>
            <p className="mt-3 font-serif italic text-lg text-[#f5f5f0]/85">{type.tagline}</p>
            <p className="mt-4 text-[15px] text-[#f5f5f0]/80 font-light leading-relaxed max-w-md mx-auto">{type.desc}</p>
            <div className="mt-6 text-left">
              <p className="text-xs uppercase tracking-[0.25em] text-[#c5a059] text-center">Your Starter Routine</p>
              <div className="mt-4 space-y-3">
                {type.picks.map((name) => {
                  const p = findProduct(name);
                  if (!p) return null;
                  return (
                    <div key={name} className="flex items-center gap-4 rounded-sm border border-white/10 bg-[#0d0d0d] p-3">
                      <img loading="lazy" decoding="async" src={p.img} alt={p.name} className="h-14 w-14 rounded-sm object-cover shrink-0" />
                      <div className="flex-1 min-w-0 font-serif text-[#f5f5f0] truncate">{p.name}</div>
                      <span className="font-serif text-[#c5a059] shrink-0">
                        {p.price ? `$${Number.isInteger(p.price) ? p.price : p.price.toFixed(2)}` : ""}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="kingtype-join"
                onClick={() => { close(); scrollTo("products"); }}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-sm bg-[#c5a059] px-8 py-4 text-sm font-medium tracking-wide text-black hover:bg-[#d4af37] transition-colors"
              >
                <Crown size={18} /> Shop My Routine
              </button>
              <button
                data-testid="kingtype-restart"
                onClick={reset}
                className="rounded-sm border border-white/15 px-6 py-4 text-sm font-medium text-[#f5f5f0]/85 hover:border-[#c5a059] hover:text-[#c5a059] transition-colors"
              >
                Retake
              </button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

const KingTypeCTA = () => {
  const [open, setOpen] = useState(false);
  return (
    <section className="max-w-4xl mx-auto px-6 lg:px-10 py-24 lg:py-32 text-center">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">A Little Fun</p>
        <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#f5f5f0]">What's Your King Type?</h2>
        <p className="mt-4 max-w-xl mx-auto text-[#f5f5f0]/80 font-light leading-relaxed">
          Every king is different. Answer four quick questions and discover your type — plus the starter routine built for you.
        </p>
        <button
          data-testid="kingtype-open"
          onClick={() => setOpen(true)}
          className="mt-8 inline-flex items-center justify-center gap-2 rounded-sm bg-[#c5a059] px-10 py-4 text-sm font-medium tracking-wide text-black hover:bg-[#d4af37] transition-colors"
        >
          <Crown size={18} /> Discover My King Type
        </button>
      </Reveal>
      <KingTypeModal open={open} onClose={() => setOpen(false)} />
    </section>
  );
};

/* ---------------- Men's Skin 101 + FAQ ---------------- */
const SKIN_101 = [
  { q: "Why does my face get oily?", a: "Your skin naturally produces oil (sebum) to protect itself. Stress, heat, and using the wrong products can push it into overdrive. A gentle daily cleanser and a lightweight, oil-free moisturizer help keep things balanced — stripping your skin actually makes it produce more oil." },
  { q: "Do men really need moisturizer?", a: "Yes. Moisturizer locks in hydration so your skin stays comfortable, looks healthier, and ages better. Even oily skin needs it — just pick an oil-free formula so it feels light, never greasy." },
  { q: "What does SPF actually do?", a: "SPF shields your skin from the sun's UV rays — the number one cause of premature aging, dark spots, and worse. A daily mineral SPF in the morning is the single easiest way to keep your skin looking younger for longer." },
  { q: "Should I exfoliate every day?", a: "No. Exfoliating 2–3 times a week is plenty. It buffs away dead skin so you look smoother and brighter, but doing it daily can leave your skin irritated and sensitive. A little goes a long way." },
  { q: "What should I use after shaving?", a: "Shaving can leave skin irritated and prone to bumps. Follow with something soothing and hydrating like our Oil-Free Moisturizer, and keep skin clean to stay smooth and comfortable. Bearded? It also hydrates the skin beneath your beard without leaving it greasy." },
  { q: "Why does my skin look tired?", a: "Lack of sleep, dehydration and sun exposure all show up on your face. Hydration from a good moisturizer and daily mineral SPF go a long way toward looking rested and refreshed." },
];

const FAQS = [
  { q: "Who is Royal King Skin Co. for?", a: "Any man who wants to look and feel his best without a complicated routine — whether you're brand new to skincare or getting back into taking care of yourself." },
  { q: "What's the simplest routine?", a: "Our four simple steps, done in about two minutes. Even that much each day makes a real difference. Tap \"Find My Royal Routine\" and we'll build yours." },
  { q: "How do I know which products to use?", a: "Take the quick Skin Advisor quiz or Ask the King — we'll point you to the right starting products for your skin and your goals, in plain English." },
  { q: "How often should I use the exfoliating products?", a: "About 2–3 times a week — not every day. Over-exfoliating can leave your skin irritated, so a little goes a long way." },
  { q: "Where are the products made?", a: "Our formulations are proudly made in the USA." },
  { q: "When will orders ship?", a: "Pre-orders are open now. Orders ship within 7–10 days, and pre-orders count toward the First 100 launch-day perks." },
  { q: "How much is shipping?", a: "Shipping is free on orders over $100 and always free on The Majestic Mane. Otherwise it's a flat $8.95. We currently ship within the USA." },
  { q: "What's your return policy?", a: "We stand behind what we make. Full return and satisfaction details will be shared at launch — join the Royal List for updates." },
];

const Accordion = ({ items, testid }) => {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div className="mt-10 divide-y divide-white/10 border-y border-white/10">
      {items.map((it, i) => (
        <div key={it.q}>
          <button
            data-testid={`${testid}-q-${i}`}
            onClick={() => setOpenIdx(openIdx === i ? null : i)}
            className="w-full flex items-center justify-between gap-4 py-5 text-left"
          >
            <span className="font-serif text-lg text-[#f5f5f0]">{it.q}</span>
            <ChevronDown size={18} className={`shrink-0 text-[#c5a059] transition-transform duration-300 ${openIdx === i ? "rotate-180" : ""}`} />
          </button>
          {openIdx === i && (
            <motion.p
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.25 }}
              className="pb-5 text-[15px] text-[#f5f5f0]/82 font-light leading-relaxed"
            >
              {it.a}
            </motion.p>
          )}
        </div>
      ))}
    </div>
  );
};

const SkinLibrary = () => (
  <section id="learn" className="max-w-4xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
    <Reveal>
      <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">Men's Skin 101</p>
      <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#f5f5f0]">Skincare, explained simply.</h2>
      <p className="mt-4 max-w-xl text-[#f5f5f0]/80 font-light leading-relaxed">
        No jargon, no lectures. Just straight answers to the questions men actually ask.
      </p>
    </Reveal>
    <Reveal delay={0.1}>
      <Accordion items={SKIN_101} testid="skin101" />
    </Reveal>
  </section>
);

const FAQ = () => (
  <section id="faq" className="border-t border-white/10 bg-[#0d0d0d] py-24 lg:py-32">
    <div className="max-w-4xl mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">Questions</p>
        <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#f5f5f0]">Frequently Asked</h2>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion items={FAQS} testid="faq" />
      </Reveal>
    </div>
  </section>
);

const StickyStart = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!show) return null;
  return (
    <button
      data-testid="sticky-start-button"
      onClick={() => scrollTo("start-here")}
      className="lg:hidden fixed bottom-6 left-6 z-[55] inline-flex items-center gap-2 rounded-full bg-[#c5a059] pl-4 pr-5 py-3 text-sm font-medium text-black shadow-[0_8px_30px_rgba(197,160,89,0.35)] active:scale-95 transition-transform"
    >
      <Crown size={18} /> Start Here
    </button>
  );
};

/* ---------------- Install App (PWA) ---------------- */
const InstallApp = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);
  const deferredRef = useRef(null);

  const isIOS =
    typeof navigator !== "undefined" &&
    /iphone|ipad|ipod/i.test(navigator.userAgent) &&
    !window.MSStream;
  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true);

  useEffect(() => {
    if (isStandalone) return;
    const dismissed = localStorage.getItem("rk_install_dismissed") === "1";
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      deferredRef.current = e;
      if (!dismissed) setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    const openHandler = () => {
      if (deferredRef.current) {
        deferredRef.current.prompt();
        deferredRef.current.userChoice.finally(() => {
          deferredRef.current = null;
        });
      } else {
        setIosOpen(true);
      }
    };
    window.addEventListener("rk-open-install", openHandler);
    let t;
    if (isIOS && !dismissed) t = setTimeout(() => setShow(true), 4000);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("rk-open-install", openHandler);
      if (t) clearTimeout(t);
    };
    // eslint-disable-next-line
  }, []);

  const dismiss = () => {
    setShow(false);
    setIosOpen(false);
    localStorage.setItem("rk_install_dismissed", "1");
  };

  const install = async () => {
    if (isIOS) {
      setIosOpen(true);
      return;
    }
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    dismiss();
  };

  if (!show && !iosOpen) return null;

  return (
    <>
      {show && !iosOpen && (
        <div
          data-testid="install-app-banner"
          className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[56] w-[92%] max-w-sm rounded-sm border border-[#c5a059]/40 bg-[#121212]/95 backdrop-blur-xl px-4 py-3 shadow-[0_20px_50px_-15px_rgba(197,160,89,0.5)] flex items-center gap-3"
        >
          <img loading="lazy" decoding="async" src="/icon-192.png" alt="Royal King Skin Co." className="h-11 w-11 rounded-sm shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-serif text-[#f5f5f0] leading-tight">Add Royal King to your home screen</p>
            <p className="text-xs text-[#f5f5f0]/70 font-light">Open it like an app — one tap, no browser.</p>
          </div>
          <button
            data-testid="install-app-button"
            onClick={install}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-sm bg-[#c5a059] px-3 py-2 text-xs font-semibold text-black hover:bg-[#d4af37] transition-colors"
          >
            {isIOS ? <Smartphone size={14} /> : <Download size={14} />} Install
          </button>
          <button
            data-testid="install-app-dismiss"
            onClick={dismiss}
            aria-label="Dismiss"
            className="shrink-0 text-[#f5f5f0]/50 hover:text-[#f5f5f0] transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {iosOpen && (
        <div
          data-testid="install-ios-modal"
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={dismiss}
        >
          <div
            className="w-full max-w-sm rounded-sm border border-[#c5a059]/40 bg-[#121212] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <img loading="lazy" decoding="async" src="/icon-192.png" alt="Royal King Skin Co." className="h-12 w-12 rounded-sm" />
              <div>
                <p className="font-serif text-lg text-[#f5f5f0]">Add to Home Screen</p>
                <p className="text-xs text-[#c5a059] uppercase tracking-wider">Royal King Skin Co.</p>
              </div>
            </div>
            <ol className="mt-5 space-y-3 text-sm text-[#f5f5f0]/85 font-light">
              {isIOS ? (
                <>
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 h-6 w-6 rounded-full bg-[#c5a059] text-black text-xs font-bold flex items-center justify-center">1</span>
                    <span>Tap the <b className="text-[#f5f5f0]">Share</b> button <Share size={14} className="inline text-[#c5a059] -mt-0.5" /> at the bottom of Safari.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 h-6 w-6 rounded-full bg-[#c5a059] text-black text-xs font-bold flex items-center justify-center">2</span>
                    <span>Scroll down and tap <b className="text-[#f5f5f0]">"Add to Home Screen"</b>.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 h-6 w-6 rounded-full bg-[#c5a059] text-black text-xs font-bold flex items-center justify-center">3</span>
                    <span>Tap <b className="text-[#f5f5f0]">Add</b> — the crown lands on your home screen.</span>
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 h-6 w-6 rounded-full bg-[#c5a059] text-black text-xs font-bold flex items-center justify-center">1</span>
                    <span>Tap the <b className="text-[#f5f5f0]">menu</b> (⋮ on Android, or the install icon in the address bar on desktop).</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 h-6 w-6 rounded-full bg-[#c5a059] text-black text-xs font-bold flex items-center justify-center">2</span>
                    <span>Choose <b className="text-[#f5f5f0]">"Install app"</b> or <b className="text-[#f5f5f0]">"Add to Home screen"</b>.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 h-6 w-6 rounded-full bg-[#c5a059] text-black text-xs font-bold flex items-center justify-center">3</span>
                    <span>Confirm — the crown lands on your device.</span>
                  </li>
                </>
              )}
            </ol>
            <button
              data-testid="install-ios-close"
              onClick={dismiss}
              className="mt-6 w-full rounded-sm bg-[#c5a059] py-3 text-sm font-semibold text-black hover:bg-[#d4af37] transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};



const LaunchDateCard = ({ token }) => {
  const [d, setD] = useState("");
  useEffect(() => { axios.get(`${API}/launch`).then((r) => setD(r.data.launch_date || "")).catch(() => {}); }, []);
  const save = async (val) => {
    try {
      await axios.put(`${API}/admin/launch`, { launch_date: val || null }, { headers: { "X-Admin-Token": token } });
      setD(val);
      toast.success(val ? "Opening day saved — countdown is live." : "Countdown cleared.");
    } catch {
      toast.error("Could not save the date.");
    }
  };
  const testEmail = async () => {
    try {
      await axios.post(`${API}/admin/launch-email/test`, {}, { headers: { "X-Admin-Token": token } });
      toast.success("Preview sent to your inbox.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the preview.");
    }
  };
  return (
    <div data-testid="admin-launch-card" className="mt-8 rounded-sm border border-[#c5a059]/30 bg-[#121212] p-5">
      <div className="text-xs uppercase tracking-widest text-[#c5a059]">Opening Day Countdown</div>
      <p className="mt-1 text-sm text-[#f5f5f0]/72">Pick the date — the clock counts down to 12:00 noon Pacific.</p>
      <div className="mt-3 flex flex-wrap gap-3">
        <input data-testid="admin-launch-date" type="date" value={d} onChange={(e) => setD(e.target.value)} className="rounded-sm bg-black border border-white/15 px-3 py-2 text-sm text-[#f5f5f0] [color-scheme:dark]" />
        <button data-testid="admin-launch-save" onClick={() => save(d)} className="rounded-sm bg-[#c5a059] px-4 py-2 text-sm font-medium text-black hover:bg-[#d4af37] transition-colors">Save</button>
        <button data-testid="admin-launch-clear" onClick={() => save("")} className="rounded-sm border border-white/15 px-4 py-2 text-sm hover:border-[#c5a059] transition-colors">Clear</button>
      </div>
      <p className="mt-4 text-sm text-[#f5f5f0]/72">When the clock hits zero, everyone on the Royal List gets the "We're LIVE" email automatically (within 15 minutes).</p>
      <button data-testid="admin-launch-email-test" onClick={testEmail} className="mt-2 rounded-sm border border-[#c5a059]/50 px-4 py-2 text-sm text-[#c5a059] hover:bg-[#c5a059]/10 transition-colors">Send me a preview of the launch email</button>
    </div>
  );
};

const StoreOpenCard = ({ token, initial, onChange }) => {
  const toggle = async () => {
    try {
      await axios.put(`${API}/admin/store-open`, { open: !initial }, { headers: { "X-Admin-Token": token } });
      toast.success(!initial ? "Store is OPEN — customers can buy now." : "Store closed until the countdown hits zero.");
      onChange();
    } catch {
      toast.error("Could not update the store.");
    }
  };
  return (
    <div data-testid="admin-store-card" className="mt-4 rounded-sm border border-[#c5a059]/30 bg-[#121212] p-5 flex flex-wrap items-center justify-between gap-4">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#c5a059]">Store</div>
        <p className="mt-1 text-sm text-[#f5f5f0]/72">The store opens by itself when the countdown hits zero. Use this to open it early.</p>
      </div>
      <button data-testid="admin-store-toggle" onClick={toggle} className={`rounded-sm px-5 py-2.5 text-sm font-semibold transition-colors ${initial ? "border border-white/20 hover:border-[#c5a059]" : "bg-[#c5a059] text-black hover:bg-[#d4af37]"}`}>
        {initial ? "Close Store (wait for countdown)" : "Open Store Now"}
      </button>
    </div>
  );
};

const ShipForm = ({ o, token, onDone }) => {
  const [carrier, setCarrier] = useState("USPS");
  const [num, setNum] = useState("");
  const [busy, setBusy] = useState(false);
  const ship = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/admin/orders/${o.session_id}/ship`, { carrier, tracking: num }, { headers: { "X-Admin-Token": token } });
      r.data.emailed ? toast.success("Marked shipped — tracking emailed to the customer.") : toast.warning(r.data.note || "Marked shipped.");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || "Could not mark shipped.");
    }
    setBusy(false);
  };
  if (o.fulfillment === "shipped") {
    return <a data-testid={`order-tracking-${o.session_id}`} href={o.tracking_url} target="_blank" rel="noreferrer" className="text-[#c5a059] underline">Shipped · {o.carrier} {o.tracking}</a>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      <select data-testid={`order-carrier-${o.session_id}`} value={carrier} onChange={(e) => setCarrier(e.target.value)} className="rounded-sm bg-black border border-white/15 px-2 py-1.5 text-xs">
        <option>USPS</option><option>UPS</option><option>FedEx</option>
      </select>
      <input data-testid={`order-tracking-input-${o.session_id}`} value={num} onChange={(e) => setNum(e.target.value)} placeholder="Tracking #" className="w-36 rounded-sm bg-black border border-white/15 px-2 py-1.5 text-xs" />
      <button data-testid={`order-ship-${o.session_id}`} disabled={busy || num.length < 4} onClick={ship} className="rounded-sm bg-[#c5a059] px-3 py-1.5 text-xs font-semibold text-black disabled:opacity-50">Mark Shipped</button>
    </div>
  );
};

const addr = (a) => (a ? [a.line1, a.line2, a.city, a.state, a.postal_code].filter(Boolean).join(", ") : "—");

const OrdersTable = ({ orders, token, onDone, vipEmails }) => (
  <>
    <h2 className="mt-10 font-serif text-xl text-[#f5f5f0]">Paid Orders ({orders.length})</h2>
    <div data-testid="admin-orders-table" className="mt-4 overflow-x-auto rounded-sm border border-white/10">
      <table className="w-full text-sm">
        <thead className="bg-[#121212] text-left text-[#c5a059]"><tr><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Ship To</th><th className="px-4 py-3">Items</th><th className="px-4 py-3">Paid</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Shipping</th></tr></thead>
        <tbody>
          {orders.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-[#f5f5f0]/50">No paid orders yet.</td></tr>}
          {orders.map((o) => (
            <tr key={o.session_id} data-testid={`order-row-${o.session_id}`} className="border-t border-white/5 align-top">
              <td className="px-4 py-3">{o.customer_name || "—"}{(vipEmails || []).includes((o.customer_email || "").toLowerCase()) && <span data-testid={`order-vip-${o.session_id}`} className="ml-2 rounded-sm bg-[#c5a059] px-1.5 py-px text-[10px] font-bold text-black">VIP</span>}<div className="text-xs text-[#f5f5f0]/55">{o.customer_email || ""}</div>{o.thank_you_code_issued && <div className="text-xs text-[#c5a059]">Gift code: {o.thank_you_code_issued}</div>}</td>
              <td className="px-4 py-3 text-xs max-w-[200px]">{addr(o.shipping_address)}</td>
              <td className="px-4 py-3">{o.items.map((i) => `${i.sku.replace(/_/g, " ")} ×${i.qty}`).join(", ")}</td>
              <td className="px-4 py-3 text-[#c5a059]">${Number(o.amount_paid ?? o.amount).toFixed(2)}</td>
              <td className="px-4 py-3">{(o.created_at || "").slice(0, 10)}</td>
              <td className="px-4 py-3"><ShipForm o={o} token={token} onDone={onDone} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </>
);

/* ---------------- Admin (Royal List) ---------------- */
const AdminPage = () => {
  const [token, setToken] = useState(localStorage.getItem("rk_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (t) => {
    setLoading(true); setError("");
    try {
      const res = await axios.get(`${API}/admin/data`, { headers: { "X-Admin-Token": t } });
      setData(res.data);
      setAuthed(true);
      localStorage.setItem("rk_admin_token", t);
    } catch (e) {
      setError("Wrong password. Try again.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(token); /* eslint-disable-next-line */ }, []);

  const exportCsv = () => {
    window.open(`${API}/admin/subscribers.csv?token=${encodeURIComponent(token)}`, "_blank");
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f0] flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 justify-center text-[#c5a059]">
            <Crown size={20} /> <span className="font-serif text-xl">Royal King Admin</span>
          </div>
          <p className="mt-2 text-center text-sm text-[#f5f5f0]/72">Enter your admin password to view your Royal List.</p>
          <input
            data-testid="admin-password-input"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(token)}
            placeholder="Admin password"
            className="mt-6 w-full rounded-sm bg-black/60 border border-white/10 px-4 py-3 text-sm focus:border-[#c5a059] focus:outline-none"
          />
          {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
          <button
            data-testid="admin-login-button"
            onClick={() => load(token)}
            disabled={loading}
            className="mt-4 w-full rounded-sm bg-[#c5a059] px-6 py-3 text-sm font-medium text-black hover:bg-[#d4af37] transition-colors disabled:opacity-60"
          >
            {loading ? "Checking…" : "View My Royal List"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f0] px-6 py-10">
      <Toaster position="top-center" theme="dark" richColors />
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-[#c5a059]">
            <Crown size={22} /> <span className="font-serif text-2xl">Royal List</span>
          </div>
          <div className="flex gap-3">
            <button data-testid="admin-refresh" onClick={() => load(token)} className="rounded-sm border border-white/15 px-4 py-2 text-sm hover:border-[#c5a059] transition-colors">Refresh</button>
            <button data-testid="admin-export-csv" onClick={exportCsv} className="rounded-sm bg-[#c5a059] px-4 py-2 text-sm font-medium text-black hover:bg-[#d4af37] transition-colors">Export CSV</button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="rounded-sm border border-white/10 bg-[#121212] p-5">
            <div className="text-3xl font-serif text-[#c5a059]" data-testid="admin-sub-count">{data?.subscriber_count ?? 0}</div>
            <div className="text-xs uppercase tracking-widest text-[#f5f5f0]/72 mt-1">Royal List Signups</div>
          </div>
          <div className="rounded-sm border border-white/10 bg-[#121212] p-5">
            <div className="text-3xl font-serif text-[#c5a059]">{data?.contact_count ?? 0}</div>
            <div className="text-xs uppercase tracking-widest text-[#f5f5f0]/72 mt-1">Contact Messages</div>
          </div>
          <div className="rounded-sm border border-[#c5a059]/30 bg-[#121212] p-5">
            <div className="text-3xl font-serif text-[#c5a059]" data-testid="admin-wholesale-count">{data?.wholesale_count ?? 0}</div>
            <div className="text-xs uppercase tracking-widest text-[#f5f5f0]/72 mt-1">Wholesale Inquiries</div>
          </div>
        </div>

        {data.dashboard && <AdminDashboard d={data.dashboard} />}
        <LaunchDateCard token={token} />
        <StoreOpenCard token={token} initial={data.store_open_override} onChange={() => load(token)} />
        {data.dashboard && <InventoryCard d={data.dashboard} token={token} onDone={() => load(token)} />}
        <OrdersTable orders={data.orders || []} token={token} onDone={() => load(token)} vipEmails={data.vip_emails} />
        <ProductPhotosCard token={token} vipOnly={data.store?.vip_only || []} onDone={() => load(token)} />
        <CampaignCard token={token} />
        <VipCard vips={data.vips || []} token={token} />
        <ManualVipCard token={token} list={data.manual_vips || []} onDone={() => load(token)} />
        <GiftCardsTable cards={data.gift_cards || []} />
        <LeaderboardCard rows={data.leaderboard || []} />
        <ReviewsAdmin reviews={data.reviews || []} token={token} onDone={() => load(token)} />

        <h2 className="mt-10 font-serif text-xl text-[#f5f5f0]">Signups</h2>
        <div className="mt-3 overflow-x-auto rounded-sm border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-[#121212] text-[#c5a059] text-left">
              <tr><th className="px-4 py-3 font-medium">Email</th><th className="px-4 py-3 font-medium">Joined</th><th className="px-4 py-3" /></tr>
            </thead>
            <tbody>
              {(data?.subscribers || []).length === 0 && (
                <tr><td colSpan="3" className="px-4 py-6 text-center text-[#f5f5f0]/62">No signups yet — they'll show up here.</td></tr>
              )}
              {(data?.subscribers || []).map((s, i) => (
                <tr key={i} data-testid={`admin-sub-row-${i}`} className="border-t border-white/5">
                  <td className="px-4 py-3">{s.email}</td>
                  <td className="px-4 py-3 text-[#f5f5f0]/72">{(s.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`admin-sub-remove-${i}`} onClick={async () => {
                      if (!window.confirm(`Remove ${s.email} from the Royal List?`)) return;
                      try { await axios.delete(`${API}/admin/subscribers/${encodeURIComponent(s.email)}`, { headers: { "X-Admin-Token": token } }); toast.success("Removed"); load(token); }
                      catch { toast.error("Could not remove."); }
                    }} className="rounded-sm border border-white/15 px-2.5 py-1 text-xs text-[#f5f5f0]/70 hover:border-red-400/60 hover:text-red-300">Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="mt-10 font-serif text-xl text-[#f5f5f0]">Wholesale Inquiries</h2>
        <div className="mt-3 overflow-x-auto rounded-sm border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-[#121212] text-[#c5a059] text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Company</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Message</th>
                <th className="px-4 py-3 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {(data?.wholesale || []).length === 0 && (
                <tr><td colSpan="5" className="px-4 py-6 text-center text-[#f5f5f0]/62">No wholesale inquiries yet — buyers will show up here.</td></tr>
              )}
              {(data?.wholesale || []).map((w, i) => (
                <tr key={i} data-testid={`admin-wholesale-row-${i}`} className="border-t border-white/5 align-top">
                  <td className="px-4 py-3">{w.name}</td>
                  <td className="px-4 py-3 text-[#f5f5f0]/80">{w.company || "—"}</td>
                  <td className="px-4 py-3">{w.email}</td>
                  <td className="px-4 py-3 text-[#f5f5f0]/72 max-w-xs">{w.message || "—"}</td>
                  <td className="px-4 py-3 text-[#f5f5f0]/72">{(w.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Prayer Wheel Banner ---------------- */
const PrayerWheelBanner = () => (
  <section data-testid="prayer-wheel-banner" className="relative border-y border-[#c5a059]/25 bg-gradient-to-b from-[#141414] to-[#0a0a0a] py-6 lg:py-8 overflow-hidden">
    <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 50% 50%, #c5a059 0%, transparent 60%)" }} />
    <div className="relative max-w-5xl mx-auto px-6 flex items-center justify-center gap-3">
      <motion.div
        initial={{ rotate: 0 }}
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: 12, ease: "linear" }}
        className="h-8 w-8 lg:h-9 lg:w-9 shrink-0"
        aria-hidden="true"
      >
        <svg viewBox="0 0 100 100" className="h-full w-full drop-shadow-[0_0_6px_rgba(197,160,89,0.4)]">
          <circle cx="50" cy="50" r="46" fill="none" stroke="#c5a059" strokeWidth="4" />
          <circle cx="50" cy="50" r="40" fill="none" stroke="#c5a059" strokeWidth="1.5" opacity="0.5" />
          <circle cx="50" cy="50" r="9" fill="none" stroke="#c5a059" strokeWidth="4" />
          <circle cx="50" cy="50" r="3" fill="#c5a059" />
          {Array.from({ length: 8 }).map((_, i) => {
            const a = (i * Math.PI) / 4;
            return (
              <line
                key={i}
                x1={50 + 9 * Math.cos(a)}
                y1={50 + 9 * Math.sin(a)}
                x2={50 + 46 * Math.cos(a)}
                y2={50 + 46 * Math.sin(a)}
                stroke="#c5a059"
                strokeWidth="3.5"
              />
            );
          })}
        </svg>
      </motion.div>
      <h2 className="font-serif text-base sm:text-lg lg:text-xl tracking-tight text-[#c5a059]">
        Keep the Prayer Wheel Rolling
      </h2>
    </div>
  </section>
);


/* ---------------- Background Music ---------------- */
const BackgroundMusic = () => {
  const audioRef = useRef(null);
  const startedRef = useRef(false);
  const [on, setOn] = useState(true);
  const [spa, setSpa] = useState(false);

  useEffect(() => {
    const audio = new Audio("/lounge-jazz.mp3");
    audio.loop = true;
    audio.preload = "none";
    audio.volume = 0.3;
    audioRef.current = audio;

    setOn(localStorage.getItem("rk-music") !== "off");

    const start = () => {
      if (startedRef.current) return;
      if (localStorage.getItem("rk-music") === "off") return;
      audio.play().then(() => { startedRef.current = true; cleanup(); }).catch(() => {});
    };
    const evs = ["pointerdown", "touchstart", "keydown", "scroll"];
    const cleanup = () => evs.forEach((e) => window.removeEventListener(e, start));
    evs.forEach((e) => window.addEventListener(e, start, { passive: true }));

    let resume = false;
    const onSpa = (e) => {
      setSpa(e.detail);
      if (e.detail) { resume = !audio.paused; audio.pause(); }
      else if (resume && localStorage.getItem("rk-music") !== "off") { audio.play().catch(() => {}); resume = false; }
    };
    window.addEventListener("rk-spa", onSpa);

    return () => { cleanup(); audio.pause(); window.removeEventListener("rk-spa", onSpa); };
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (on) {
      audio.pause();
      localStorage.setItem("rk-music", "off");
      setOn(false);
    } else {
      localStorage.setItem("rk-music", "on");
      audio.volume = 0.3;
      audio.play().then(() => { startedRef.current = true; }).catch(() => {});
      setOn(true);
    }
  };

  return (
    <button
      data-testid="music-toggle"
      onClick={toggle}
      aria-label={on ? "Pause lounge music" : "Play lounge music"}
      className="fixed left-4 bottom-24 sm:bottom-6 z-[60] flex items-center gap-2 rounded-full border border-[#c5a059]/50 bg-black/70 backdrop-blur px-3.5 py-2.5 text-[#c5a059] hover:border-[#c5a059] hover:bg-black/90 transition-colors shadow-[0_0_20px_rgba(197,160,89,0.25)]"
    >
      <span className="relative flex h-[18px] w-[18px] items-center justify-center">
        {on && !spa && <span className="absolute inline-flex h-full w-full rounded-full bg-[#c5a059]/30 animate-ping" />}
        {on && !spa ? <Volume2 size={18} className="relative" /> : <VolumeX size={18} className="relative" />}
      </span>
      <span className="hidden sm:inline text-[11px] uppercase tracking-[0.15em] font-medium">{spa ? "Jazz Paused" : on ? "Jazz On" : "Jazz Off"}</span>
    </button>
  );
};


function App() {
  const path = typeof window !== "undefined" ? window.location.pathname.replace(/\/$/, "") : "";
  if (path === "/admin") return <AdminPage />;
  if (path === "/payment/success") return <PaymentResult />;
  if (path === "/payment/cancel") return <PaymentResult cancelled />;
  if (path === "/review") return <ReviewPage />;
  if (path.startsWith("/guides/")) return <GuidePage slug={path.slice(8)} />;
  return (
    <StoreProvider>
    <div className="App grain bg-[#0a0a0a] text-[#f5f5f0]">
      <Toaster position="top-center" theme="dark" richColors />
      <CartDrawer />
      <BackgroundMusic />
      <Header />
      <Hero />
      <PrayerWheelBanner />
      <Marquee />
      <OriginStory />
      <MissionBand />
      <StartHere />
      <RoyalRoutine />
      <GrandOpeningOffer />
      <HolidayBanner />
      <GiftGuide />
      <RoyalRitual />
      <Products />
      <Merch />
      <ReviewHighlights />
      <GiftCards />
      <RoutineCTA />
      <EditorialBanner img={IMG.manWash1} overline="The Daily Ritual" quote="Your throne is earned every morning." />
      <Ingredients />
      <InsideTheJar />
      <ShippingInfo />
      <About />
      <Founder />
      <KingsCode />
      <EditorialBanner img={IMG.manWash2} overline="Confidence, Refined" quote="Great skin is the mark of a king." />
      <Reviews />
      <KingTypeCTA />
      <SkinLibrary />
      <GuidesSection />
      <SeoContent />
      <RetailPartners />
      <FAQ />
      <Waitlist />
      <Footer />
      <AskTheKing />
      <StickyStart />
      <InstallApp />
    </div>
    </StoreProvider>
  );
}

export default App;
